import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, TAC_RATE } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { usePricingRates } from '../hooks/usePricing';
import { priceLoadFailedMessage, PRICE_LOADING_MESSAGE } from '../utils/pricing';
import { generateBookingReference } from '../utils/bookingReference';
import { downloadTicket } from '../utils/ticketPDF';
import { Zap, Footprints, AlertTriangle, Lock, MessageCircle, Users, ChevronRight, Ban, Link2, ClipboardList } from 'lucide-react';
import SendTicketModal from './SendTicketModal';
import SendLinkModal from './SendLinkModal';
import { TableLinksPanel } from './TableLinks';
import GuestListEditor, { persistGuestList, hasGuestData } from './GuestListEditor';

// ─── Layout constants ─────────────────────────────────────────────────────────

const MIDDLE_LEFT  = ['L1A','L1B','L2','L3','L4','L5','L6A','L6B','L7A','L7B'];
const MIDDLE_RIGHT = ['R1A','R1B','R2','R3','R4','R5','R6A','R6B','R7A','R7B'];
// Upper deck spreadsheet columns: Front side (left) and Back side (right)
const UPPER_LEFT_COL  = ['1A','1B','2','3','4','5','6','7','8','9A','9B'];
const UPPER_RIGHT_COL = ['10','11','12','13','14','15'];

const MIDDLE_PAIRS = MIDDLE_LEFT.map((l, i) => [l, MIDDLE_RIGHT[i]]);
const UPPER_PAIRS  = UPPER_LEFT_COL.map((l, i) => [l, UPPER_RIGHT_COL[i] ?? null]);

const SHOW_UPPER = ['Sunset Cruise','Sundown Cruise']; // fallback; overridden by cruise settings

// All upper tables (flat) — used for auto-assign
const ALL_UPPER = [...UPPER_LEFT_COL, ...UPPER_RIGHT_COL];

const ADJ_MIDDLE = [
  ['L1A','L1B'],['L2','L3'],['L4','L5'],['L6A','L6B'],['L7A','L7B'],
  ['R1A','R1B'],['R2','R3'],['R4','R5'],['R6A','R6B'],['R7A','R7B'],
];
const ADJ_UPPER = [
  ['1A','1B'],['1B','2'],['2','3'],['3','4'],['4','5'],['5','6'],['6','7'],['7','8'],['8','9A'],['9A','9B'],
  ['10','11'],['11','12'],['12','13'],['13','14'],['14','15'],
];

// ─── Date helpers ─────────────────────────────────────────────────────────────

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = n => { const s=['th','st','nd','rd'],v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
const toDisplayDate = d => { if(!d) return ''; const[y,m,dd]=d.split('-'); return `${dd}-${m}-${y}`; };
const toInputDate   = d => { if(!d) return ''; const[dd,m,y]=d.split('-'); return `${y}-${m}-${dd}`; };
const formatDateWords = d => { if(!d) return ''; const[y,m,dd]=d.split('-').map(Number); return `${ordinal(dd)} ${MONTHS[m-1]} ${y}`; };
// Local-timezone today (toISOString is UTC — before 05:30 IST it returns yesterday)
const todayInput = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// ─── Pax helpers ──────────────────────────────────────────────────────────────

const paxCount   = b => (b.adults||0)+(b.children||0)+(b.tour_leaders||0);
const paxVisible = b => (b.adults||0)+(b.children||0); // for summary bar

// ─── Auto-assign ──────────────────────────────────────────────────────────────

function computeAutoAssign({ unassignedBookings, tableInventory, extraTables, overrides, assignments, cruise }) {
  const assignedSet = new Set(assignments.map(a => a.table_name));
  const overrideMap = {};
  overrides.forEach(o => { overrideMap[o.table_name] = o; });

  const getCap = name => {
    if (overrideMap[name]) return { min: overrideMap[name].min_capacity, max: overrideMap[name].max_capacity };
    const t = [...tableInventory, ...extraTables].find(t => (t.name || t.table_name) === name);
    return t ? { min: t.min_capacity, max: t.max_capacity } : { min: 0, max: 99 };
  };

  const showUpper = SHOW_UPPER.includes(cruise);
  const layoutForDeck = deck => {
    const base = deck === 'Upper Deck'
      ? (showUpper ? ALL_UPPER : [])
      : [...MIDDLE_LEFT, ...MIDDLE_RIGHT];
    const extras = extraTables.filter(t => t.deck === deck).map(t => t.table_name || t.name);
    return [...new Set([...base, ...extras])];
  };

  const sorted = [...unassignedBookings].sort((a,b) => paxCount(b) - paxCount(a));
  const preview = [];
  const tentative = new Set(assignedSet);

  for (const booking of sorted) {
    const pax = paxCount(booking);
    const deck = booking.deck || 'Middle Deck';
    const avail = layoutForDeck(deck).filter(n => !tentative.has(n));
    const availSet = new Set(avail);

    const single = avail.find(n => { const c=getCap(n); return pax>=c.min && pax<=c.max; });
    if (single) { tentative.add(single); preview.push({ booking, tables:[single], success:true }); continue; }

    const adjPairs = deck === 'Upper Deck' ? ADJ_UPPER : ADJ_MIDDLE;
    let pair = null;
    for (const [t1,t2] of adjPairs) {
      if (!availSet.has(t1)||!availSet.has(t2)) continue;
      const c1=getCap(t1), c2=getCap(t2);
      if (pax>=c1.min && pax<=c1.max+c2.max) { pair=[t1,t2]; break; }
    }
    if (pair) { pair.forEach(t=>tentative.add(t)); preview.push({ booking, tables:pair, success:true }); continue; }

    preview.push({ booking, tables:[], success:false });
  }
  return preview;
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const labelStyle = {
  display:'block', fontSize:'0.68rem', fontWeight:700,
  color:'var(--color-ink-muted)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5,
};
const inputStyle = {
  width:'100%', background:'#ffffff', border:'1.5px solid var(--color-border-strong)',
  borderRadius:8, padding:'9px 11px', color:'#0A1628', fontSize:'0.88rem',
  outline:'none', boxSizing:'border-box',
};

// ─── Section header row ───────────────────────────────────────────────────────

function SectionHeader({ label, colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan || 16} style={{
        background: NAVY, color:'#ffffff', fontWeight:800,
        fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'1.5px',
        padding:'6px 14px', userSelect:'none',
      }}>
        {label}
      </td>
    </tr>
  );
}

// ─── Modal: Capacity Override ─────────────────────────────────────────────────

function CapacityOverrideModal({ tableName, current, onSave, onClose }) {
  const [minCap, setMinCap] = useState(current.min ?? '');
  const [maxCap, setMaxCap] = useState(current.max ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!minCap || !maxCap || Number(minCap)>Number(maxCap)) return;
    setSaving(true);
    await onSave(Number(minCap), Number(maxCap));
    setSaving(false);
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:340 }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:4 }}>Override Capacity</h3>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:18 }}>
          Override for <strong>{tableName}</strong> on this date and cruise only.
        </p>
        <div style={{ display:'flex', gap:12, marginBottom:20 }}>
          <div style={{ flex:1 }}>
            <label style={labelStyle}>Min</label>
            <input type="number" min="0" value={minCap} onChange={e=>setMinCap(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex:1 }}>
            <label style={labelStyle}>Max</label>
            <input type="number" min="0" value={maxCap} onChange={e=>setMaxCap(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-gold" onClick={handleSave} disabled={saving||!minCap||!maxCap}>
            {saving ? 'Saving…' : 'Save Override'}
          </button>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Extra Table ───────────────────────────────────────────────────────

function ExtraTableModal({ onSave, onClose }) {
  const [deck, setDeck]       = useState('Middle Deck');
  const [name, setName]       = useState('');
  const [minC, setMinC]       = useState('');
  const [maxC, setMaxC]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const valid = !!(name.trim() && minC && maxC && Number(minC)<=Number(maxC));

  const handleSave = async e => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setSaveErr('');
    try {
      // column is table_name in Supabase, not name
      await onSave({ deck, table_name: name.trim(), min_capacity: Number(minC), max_capacity: Number(maxC) });
    } catch (err) {
      setSaveErr(err.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:360 }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:16 }}>Add Extra Table</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Deck</label>
            <select value={deck} onChange={e=>setDeck(e.target.value)} style={inputStyle}>
              <option>Middle Deck</option>
              <option>Upper Deck</option>
            </select>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Table Name</label>
            <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. F1" style={inputStyle} autoFocus />
          </div>
          <div>
            <label style={labelStyle}>Min Pax</label>
            <input type="number" min="0" value={minC} onChange={e=>setMinC(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Max Pax</label>
            <input type="number" min="0" value={maxC} onChange={e=>setMaxC(e.target.value)} style={inputStyle} />
          </div>
        </div>
        {saveErr && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', fontSize:'0.78rem', color:'#dc2626', marginBottom:12 }}>
            {saveErr}
          </div>
        )}
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-gold" onClick={handleSave} disabled={saving||!valid}>
            {saving ? 'Adding…' : 'Add Table'}
          </button>
          <button className="btn btn-outline" onClick={onClose} type="button">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Auto-Assign Preview ───────────────────────────────────────────────

function AutoAssignModal({ preview, onAccept, onClose, accepting }) {
  const succeeded = preview.filter(p=>p.success);
  const failed    = preview.filter(p=>!p.success);
  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:480, maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:4, flexShrink:0 }}>Auto-Assign Preview</h3>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:16, flexShrink:0 }}>
          {succeeded.length} booking{succeeded.length!==1?'s':''} can be assigned.
          {failed.length>0 && <span style={{ color:'#DC2626', fontWeight:600 }}> {failed.length} cannot be placed.</span>}
        </p>
        <div style={{ overflowY:'auto', flex:1, marginBottom:16 }}>
          {succeeded.map((p,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--color-border-subtle)' }}>
              <span style={{ fontSize:'0.85rem', color:'#1a2a40', fontWeight:600 }}>{p.booking.booking_name}</span>
              <span style={{ fontSize:'0.78rem', color:'var(--color-accent-strong)', fontWeight:600 }}>→ {p.tables.join(' + ')}</span>
            </div>
          ))}
          {failed.map((p,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--color-border-subtle)' }}>
              <span style={{ fontSize:'0.85rem', color:'#DC2626', fontWeight:600 }}>{p.booking.booking_name}</span>
              <span style={{ fontSize:'0.72rem', color:'#DC2626' }}>No suitable table</span>
            </div>
          ))}
          {preview.length===0 && <p style={{ fontSize:'0.85rem', color:'var(--color-ink-faint)', textAlign:'center', padding:'24px 0' }}>All bookings already assigned.</p>}
        </div>
        <div style={{ display:'flex', gap:10, flexShrink:0 }}>
          <button className="btn btn-gold" onClick={onAccept} disabled={accepting||succeeded.length===0}>
            {accepting ? 'Applying…' : 'Accept All'}
          </button>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Table Action ──────────────────────────────────────────────────────

function TableActionModal({ tableName, booking, assignment, bookingTableCount, onReassign, onUnassign, onClose, onRainMove, onEndRain, rainTables }) {
  const [confirmMulti, setConfirmMulti] = useState(false);
  const isRainRow = !!assignment?.is_rain;
  // Rain move offered from the booking's REAL Upper Deck row: not already a
  // rain row, booking lives upstairs, and no rain rows exist yet.
  const canRainMove = !!onRainMove && !isRainRow && booking?.deck === 'Upper Deck' && (rainTables || []).length === 0;

  const handleUnassign = () => {
    if (bookingTableCount>1 && !confirmMulti) { setConfirmMulti(true); return; }
    onUnassign(false);
  };

  const paxLabel = b => {
    if (!b) return '';
    const parts=[];
    if ((b.adults||0)>0) parts.push(`${b.adults}A`);
    if ((b.children||0)>0) parts.push(`${b.children}C`);
    if ((b.tour_leaders||0)>0) parts.push(`${b.tour_leaders}TL`);
    return parts.join(' ')||'0';
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:340 }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:4 }}>Row {tableName}</h3>
        <p style={{ fontSize:'0.85rem', color:'#4a5a7a', marginBottom:4, fontWeight:600 }}>{booking?.booking_name}</p>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:16 }}>
          {paxLabel(booking)} · {booking?.deck}
          {isRainRow && ' · temporary rain seating'}
          {!isRainRow && (rainTables||[]).length > 0 && ` · reseated to ${rainTables.join(', ')} (rain)`}
        </p>
        {confirmMulti ? (
          <>
            <p style={{ fontSize:'0.82rem', color:'#92400e', background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:8, padding:'10px 12px', marginBottom:16 }}>
              This booking spans {bookingTableCount} rows. Unassign all?
            </p>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="btn btn-danger btn-sm" onClick={()=>onUnassign(true)}>Unassign All</button>
              <button className="btn btn-outline btn-sm" onClick={()=>onUnassign(false)}>This Row Only</button>
              <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {isRainRow ? (
              <>
                <button className="btn btn-sm" style={{ background:'#0C4A6E', color:'#fff', border:'none' }} onClick={onEndRain}>
                  End Rain Seating
                </button>
                <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
              </>
            ) : (
              <>
                <button className="btn btn-navy btn-sm" onClick={onReassign}>Reassign</button>
                <button className="btn btn-danger btn-sm" onClick={handleUnassign}>Unassign</button>
                {canRainMove && (
                  <button className="btn btn-sm" style={{ background:'#0C4A6E', color:'#fff', border:'none' }} onClick={onRainMove}>
                    Move Downstairs (Rain)
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal: Confirm / Warning dialog ─────────────────────────────────────────

function ConfirmDialog({ heading, message, actions, onClose }) {
  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:420 }}>
        <h3 style={{ fontSize:'1rem', fontWeight:800, color:NAVY, marginBottom:10 }}>{heading}</h3>
        {message && (
          <p style={{ fontSize:'0.88rem', color:'#4a5a7a', marginBottom:24, lineHeight:1.55 }}>{message}</p>
        )}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {actions.map(({ label, style: s, onClick })=>(
            <button key={label} style={{
              padding:'9px 20px', borderRadius:8, fontWeight:700, fontSize:'0.85rem',
              cursor:'pointer', border:'none', ...s,
            }} onClick={onClick}>{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Block Tables ──────────────────────────────────────────────────────

// Creates one table_blocks row per selected table for an inclusive date range.
// The 31-day cap mirrors the DB CHECK constraint so staff see a friendly
// message instead of a raw constraint violation. Blocks are cruise-scoped:
// they inherit the sheet's selected cruise automatically; "Apply to all
// cruises" (maintenance/damage) saves cruise_type null instead, which every
// enforcement site treats as all-cruises. A reason is required (DB NOT NULL).
function BlockTablesModal({ selectedTables, defaultDate, cruise, onSave, onClose }) {
  const [startDate,  setStartDate]  = useState(defaultDate);
  const [endDate,    setEndDate]    = useState(defaultDate);
  const [reason,     setReason]     = useState('');
  const [allCruises, setAllCruises] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saveErr,    setSaveErr]    = useState('');

  const tableList = [...selectedTables];
  const spanDays = (startDate && endDate)
    ? Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1
    : 0;
  const rangeErr = !startDate || !endDate ? ''
    : spanDays < 1 ? 'End date must be on or after the start date.'
    : spanDays > 31 ? `Blocks are capped at 31 days — this range is ${spanDays} days.`
    : '';
  const reasonErr = reasonTouched && !reason.trim() ? 'A reason is required.' : '';
  const valid = !!startDate && !!endDate && !rangeErr && !!reason.trim();

  const handleSave = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setSaveErr('');
    try {
      await onSave({ startDate, endDate, reason: reason.trim(), allCruises });
    } catch (err) {
      setSaveErr(err.message || 'Could not save the block.');
      setSaving(false);
    }
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:400 }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:4 }}>
          Block {tableList.length} Table{tableList.length!==1?'s':''}
        </h3>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:16 }}>
          <strong>{tableList.join(', ')}</strong> — blocked for {allCruises ? <strong>every cruise</strong> : <strong>{cruise}</strong>} on these dates.
          Existing seating is unaffected; no new staff, walk-in, or online booking can take a blocked table.
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={{ ...inputStyle, colorScheme:'light' }} />
          </div>
          <div>
            <label style={labelStyle}>To (inclusive)</label>
            <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={{ ...inputStyle, colorScheme:'light' }} />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Reason</label>
            <input type="text" value={reason} onChange={e=>setReason(e.target.value)} onBlur={()=>setReasonTouched(true)}
              placeholder="e.g. Broken table, private reservation"
              style={{ ...inputStyle, ...(reasonErr ? { borderColor:'#DC2626' } : {}) }} />
          </div>
          <label style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8, fontSize:'0.78rem', fontWeight:600, color:NAVY, cursor:'pointer', userSelect:'none' }}>
            <input type="checkbox" checked={allCruises} onChange={e=>setAllCruises(e.target.checked)} style={{ margin:0 }} />
            Apply to all cruises (maintenance / damage)
          </label>
        </div>
        {(rangeErr || reasonErr || saveErr) && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', fontSize:'0.78rem', color:'#dc2626', marginBottom:12 }}>
            {rangeErr || reasonErr || saveErr}
          </div>
        )}
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-gold" onClick={handleSave} disabled={saving||!valid}>
            {saving ? 'Blocking…' : `Block ${spanDays === 1 ? 'for This Date' : `for ${spanDays} Days`}`}
          </button>
          <button className="btn btn-outline" onClick={onClose} type="button">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Walk-in Booking ───────────────────────────────────────────────────

function WalkInModal({ selectedTables, getCap, displayDate, cruise, deck, onSave, onClose }) {
  const [partners, setPartners]           = useState([]);  // [{name, phone_number}]
  const [bookingName, setBookingName]     = useState('');
  const [partnerName, setPartnerName]     = useState('');
  const [partnerPhone, setPartnerPhone]   = useState('');
  const [partnerSugg, setPartnerSugg]     = useState([]);
  const [guestPhone, setGuestPhone]       = useState('');
  const [adults, setAdults]               = useState('');
  const [children, setChildren]           = useState(0);
  const [infants, setInfants]             = useState(0);
  const [tourLeaders, setTourLeaders]     = useState(0);
  // Food preferences (Dinner Cruise buffet only). Hidden + saved as 0 otherwise.
  const [foodNonVeg, setFoodNonVeg]       = useState(0);
  const [foodVeg, setFoodVeg]             = useState(0);
  const [foodJain, setFoodJain]           = useState(0);
  const [totalAmount, setTotalAmount]     = useState('');
  const [tacManual, setTacManual]         = useState(false);
  const [tac, setTac]                     = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('Confirmed');
  const [razorpayAmt, setRazorpayAmt]     = useState(0);
  const [cashAmt, setCashAmt]             = useState(0);
  const [iciciAmt, setIciciAmt]           = useState(0);
  const [bankTransferAmt, setBankTransferAmt] = useState(0);
  const [gpayAmt, setGpayAmt]             = useState(0);
  const [payWarning, setPayWarning]       = useState('');
  const [notes, setNotes]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [confirmNonConfirmed, setConfirmNonConfirmed] = useState(false);
  // Optional guest list (deferred — persisted after the walk-in row is created).
  const [guestsOpen, setGuestsOpen]       = useState(false);
  const [guestSnapshot, setGuestSnapshot] = useState(null); // { primary, additional, cap }

  useEffect(() => {
    supabase.from('partners').select('id, name, phone_number').is('archived_at', null).order('name')  // archived partners never suggested
      .then(({ data }) => setPartners(data || []));
  }, []);

  // Live per-head rates for THIS cruise + deck from the `pricing` table (see
  // hooks/usePricing.js — the payment edge function's create-order read).
  // Never a hardcoded constant: that drifted silently whenever a price was
  // edited in the Pricing screen. `rates` is null until both rows arrive and
  // the adult rate is positive; `ratesStatus` drives the UI and the save guard.
  const { rates, status: ratesStatus, retry: retryRates } = usePricingRates(cruise, deck);

  useEffect(() => {
    // FOC bookings are free: total pinned to 0 (DailyTracker applyBookingRecalc
    // convention). The early return also keeps the auto>0 guard below from
    // re-pricing on a pax/deck change while FOC is selected; switching FOC off
    // re-runs this effect and restores the computed price.
    if (paymentStatus === 'FOC') { setTotalAmount('0'); return; }
    // No live rates (loading or failed) → leave the total alone. Never price
    // from a constant and never from zero; the save guard refuses instead.
    if (!rates) return;
    const auto = (Number(adults)||0)*rates.adult + (Number(children)||0)*rates.child;
    if (auto > 0) setTotalAmount(String(auto));
  }, [rates, adults, children, paymentStatus]);

  useEffect(() => {
    // FOC: no commission on a free booking, even when a partner is set or TAC
    // was manually edited.
    if (paymentStatus === 'FOC') { setTac(0); return; }
    if (!tacManual) {
      const total = Number(totalAmount) || 0;
      setTac(partnerName.trim() ? Math.round(total * TAC_RATE) : 0);
    }
  }, [partnerName, totalAmount, tacManual, paymentStatus]);

  useEffect(() => { if (!partnerName.trim()) { setTacManual(false); } }, [partnerName]);

  const netTotal = (Number(totalAmount)||0) - (Number(tac)||0);

  // Food preferences apply ONLY to the Dinner Cruise; must total adults + children.
  const isDinnerCruise = (cruise || '').toLowerCase().includes('dinner');
  const foodRequired = (Number(adults) || 0) + (Number(children) || 0);
  const foodTotal = (Number(foodNonVeg) || 0) + (Number(foodVeg) || 0) + (Number(foodJain) || 0);
  const foodValid = !isDinnerCruise || foodTotal === foodRequired;

  const capRange = useMemo(() => {
    let minT = 0, maxT = 0;
    selectedTables.forEach(t => { const c = getCap(t); if (c) { minT += c.min; maxT += c.max; } });
    return { min: minT, max: maxT };
  }, [selectedTables, getCap]);

  const totalPax = (Number(adults)||0) + (Number(children)||0) + (Number(tourLeaders)||0);
  const capColor = !totalPax ? '#9a8e7a'
    : totalPax >= capRange.min && totalPax <= capRange.max ? '#15803d'
    : totalPax <= capRange.max * 1.25 ? '#D97706' : '#DC2626';
  const capBg = !totalPax ? '#f8f7f4'
    : totalPax >= capRange.min && totalPax <= capRange.max ? '#DCFCE7'
    : totalPax <= capRange.max * 1.25 ? '#FEF3C7' : '#FEE2E2';

  const handlePartnerInput = val => {
    setPartnerName(val);
    setPartnerSugg(val.trim().length < 1 ? [] :
      partners.filter(p => p.name.toLowerCase().includes(val.toLowerCase())).slice(0, 5));
  };

  const handlePartnerSelect = partner => {
    setPartnerName(partner.name);
    if (partner.phone_number) setPartnerPhone(partner.phone_number);
    setPartnerSugg([]);
  };

  const doSave = async () => {
    setSaving(true); setError('');
    try {
      // Link the partner record when the typed name matches exactly one
      // partner (case/space-insensitive); anything uncertain stays unlinked.
      const pnKey = partnerName.trim().toLowerCase();
      const pnMatches = pnKey ? partners.filter(p => (p.name || '').trim().toLowerCase() === pnKey) : [];
      await onSave({ bookingName: bookingName.trim(), partnerName: partnerName.trim()||null, partnerId: pnMatches.length === 1 ? pnMatches[0].id : null, partnerPhone: partnerPhone.trim()||null, guestPhone: guestPhone.trim()||null, adults: Number(adults)||0, children: Number(children)||0, infants: Number(infants)||0, tourLeaders: Number(tourLeaders)||0, foodNonVeg: isDinnerCruise ? (Number(foodNonVeg)||0) : 0, foodVeg: isDinnerCruise ? (Number(foodVeg)||0) : 0, foodJain: isDinnerCruise ? (Number(foodJain)||0) : 0, totalAmount: Number(totalAmount)||0, tac: Number(tac)||0, netTotal, paymentStatus, razorpayAmt: Number(razorpayAmt)||0, cashAmt: Number(cashAmt)||0, iciciAmt: Number(iciciAmt)||0, bankTransferAmt: Number(bankTransferAmt)||0, gpayAmt: Number(gpayAmt)||0, notes: notes.trim()||null, guestSnapshot });
    } catch (err) { setError(err.message||'Save failed.'); setSaving(false); }
  };

  const handleSave = async () => {
    if (!bookingName.trim() || !adults) { setError('Booking name and adults are required.'); return; }
    // Hard stop: no live price, no walk-in. A walk-in charged the wrong amount
    // is worse than one that cannot be recorded for a moment — so this refuses
    // (including manually typed totals) rather than guessing. FOC is exempt:
    // its total is pinned to 0 regardless of the rate card.
    if (paymentStatus !== 'FOC' && ratesStatus !== 'ok') {
      setError(ratesStatus === 'loading'
        ? PRICE_LOADING_MESSAGE
        : priceLoadFailedMessage(cruise, deck, 'The walk-in'));
      return;
    }
    // Dinner Cruise: food preferences must total adults + children.
    if (isDinnerCruise && foodTotal !== foodRequired) {
      setError(`Food preferences must total ${foodRequired} (adults + children).`); return;
    }
    if (paymentStatus === 'Confirmed') {
      // Confirmed bookings must have payment amounts matching the net total.
      const paySum = (Number(razorpayAmt)||0) + (Number(cashAmt)||0) + (Number(iciciAmt)||0) + (Number(bankTransferAmt)||0) + (Number(gpayAmt)||0);
      if (paySum !== netTotal) {
        setPayWarning(`Payment amounts (₹${paySum.toLocaleString('en-IN')}) don't match Net Total (₹${netTotal.toLocaleString('en-IN')}). Please check.`);
        return;
      }
      setPayWarning('');
      await doSave();
    } else {
      // Non-confirmed bookings need manager confirmation before seating.
      setPayWarning('');
      setConfirmNonConfirmed(true);
    }
  };

  const tableList = [...selectedTables].join(', ');

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:560, maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:2 }}>Walk-in Booking</h3>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:16 }}>
          {displayDate} · {cruise} · <strong>{deck}</strong> · Tables: <strong>{tableList}</strong>
        </p>
        <div style={{ overflowY:'auto', flex:1, paddingRight:4 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {/* Booking Name — full width */}
            <div style={{ gridColumn:'1/-1' }}>
              <label style={labelStyle}>Booking Name <span style={{ color:'#DC2626' }}>*</span></label>
              <input type="text" value={bookingName} onChange={e=>setBookingName(e.target.value)} placeholder="Guest or company name" style={inputStyle} autoFocus />
            </div>
            {/* Partner Name with autocomplete */}
            <div style={{ position:'relative' }}>
              <label style={labelStyle}>Partner Name</label>
              <input type="text" value={partnerName} onChange={e=>handlePartnerInput(e.target.value)} placeholder="Travel agent (optional)" style={inputStyle} autoComplete="off" />
              {partnerSugg.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid var(--color-border-strong)', borderRadius:8, zIndex:100, boxShadow:'0 4px 12px rgba(10,22,40,0.12)', overflow:'hidden' }}>
                  {partnerSugg.map(p=>(
                    <button key={p.name} style={{ display:'block', width:'100%', padding:'8px 12px', textAlign:'left', background:'none', border:'none', cursor:'pointer', fontSize:'0.85rem', color:NAVY }}
                      onMouseDown={e=>{ e.preventDefault(); handlePartnerSelect(p); }}>
                      <div style={{ fontWeight:600 }}>{p.name}</div>
                      {p.phone_number && <div style={{ fontSize:'0.72rem', color:'var(--color-ink-muted)', marginTop:1 }}>{p.phone_number}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Guest Phone — right next to Partner Name */}
            <div>
              <label style={labelStyle}>Guest Phone</label>
              <input type="text" value={guestPhone} onChange={e=>setGuestPhone(e.target.value)} placeholder="+91…" style={inputStyle} />
            </div>
            {/* Partner Phone — after guest phone */}
            <div>
              <label style={labelStyle}>Partner Phone</label>
              <input type="text" value={partnerPhone} onChange={e=>setPartnerPhone(e.target.value)} placeholder="+91…" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Adults <span style={{ color:'#DC2626' }}>*</span></label>
              <input type="number" min="1" value={adults} onChange={e=>setAdults(e.target.value)} placeholder="0" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Children</label>
              <input type="number" min="0" value={children} onChange={e=>setChildren(e.target.value)} placeholder="0" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Infants</label>
              <input type="number" min="0" value={infants} onChange={e=>setInfants(e.target.value)} placeholder="0" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Tour Leaders</label>
              <input type="number" min="0" value={tourLeaders} onChange={e=>setTourLeaders(e.target.value)} placeholder="0" style={inputStyle} />
            </div>
            {/* Food preferences — Dinner Cruise buffet only */}
            {isDinnerCruise && (
              <div style={{ gridColumn:'1/-1' }}>
                <label style={labelStyle}>Food preferences (buffet) <span style={{ color:'#DC2626' }}>*</span></label>
                <div style={{ display:'flex', gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--color-ink-muted)', marginBottom:3 }}>Non-veg</div>
                    <input type="number" min="0" value={foodNonVeg} onChange={e=>setFoodNonVeg(e.target.value)} placeholder="0" style={inputStyle} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--color-ink-muted)', marginBottom:3 }}>Veg</div>
                    <input type="number" min="0" value={foodVeg} onChange={e=>setFoodVeg(e.target.value)} placeholder="0" style={inputStyle} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--color-ink-muted)', marginBottom:3 }}>Jain</div>
                    <input type="number" min="0" value={foodJain} onChange={e=>setFoodJain(e.target.value)} placeholder="0" style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginTop:6, fontSize:'0.78rem', fontWeight:600, color: foodValid ? 'var(--color-ink-muted)' : '#dc2626' }}>
                  {foodValid
                    ? `Total ${foodTotal} of ${foodRequired} (adults + children).`
                    : `Food preferences must total ${foodRequired} (adults + children). Currently ${foodTotal}.`}
                </div>
              </div>
            )}
            <div>
              <label style={labelStyle}>Total Amount (₹)</label>
              <input type="number" min="0" value={totalAmount} onChange={e=>setTotalAmount(e.target.value)} placeholder="Auto-calculated" style={inputStyle} />
              {ratesStatus === 'ok' && (
                <span style={{ fontSize:'0.68rem', color:'var(--color-ink-muted)', marginTop:3, display:'block' }}>
                  ₹{rates.adult.toLocaleString('en-IN')} adult · ₹{rates.child.toLocaleString('en-IN')} child
                </span>
              )}
              {ratesStatus === 'loading' && (
                <span style={{ fontSize:'0.68rem', color:'var(--color-ink-muted)', marginTop:3, display:'block' }}>Loading price…</span>
              )}
            </div>
            <div>
              <label style={labelStyle}>TAC (₹)</label>
              <input type="number" min="0" value={tac} onChange={e=>{ setTac(e.target.value); setTacManual(true); }} placeholder="0" style={inputStyle} />
              <span style={{ fontSize:'0.68rem', color:'var(--color-ink-muted)', marginTop:3, display:'block' }}>10% when partner set</span>
            </div>
            <div>
              <label style={labelStyle}>Net Total (₹)</label>
              <input type="number" value={isNaN(netTotal)?'':netTotal} readOnly style={{ ...inputStyle, color:'var(--color-accent-strong)', fontWeight:700, background:'var(--color-sunken)' }} />
            </div>
            <div>
              <label style={labelStyle}>Payment Status</label>
              <select value={paymentStatus} onChange={e=>{
                const v = e.target.value;
                setPaymentStatus(v); setPayWarning('');
                // The breakdown inputs below unmount for other statuses but their
                // state would survive and be saved — and log_booking_collections
                // has no payment-status filter, so stale amounts would log
                // collections against a non-collecting (e.g. FOC) booking.
                if (v !== 'Confirmed' && v !== 'Balance') {
                  setRazorpayAmt(0); setCashAmt(0); setIciciAmt(0); setBankTransferAmt(0); setGpayAmt(0);
                }
              }} style={inputStyle}>
                <option value="Confirmed">Confirmed</option>
                <option value="Pending">Pending</option>
                <option value="Balance">Balance</option>
                <option value="FOC">FOC</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            {/* Payment Breakdown — visible for Confirmed or Balance */}
            {(paymentStatus === 'Confirmed' || paymentStatus === 'Balance') && (
              <div style={{ gridColumn:'1/-1' }}>
                <label style={labelStyle}>Payment Breakdown (₹)</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))', gap:8, marginTop:4 }}>
                  {[
                    ['Razorpay', razorpayAmt, setRazorpayAmt],
                    ['Cash', cashAmt, setCashAmt],
                    ['ICICI POS', iciciAmt, setIciciAmt],
                    ['Bank Transfer', bankTransferAmt, setBankTransferAmt],
                    ['GPay', gpayAmt, setGpayAmt],
                  ].map(([label, val, setter]) => (
                    <div key={label}>
                      <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--color-ink-muted)', marginBottom:3 }}>{label}</div>
                      <input type="number" min="0" value={val} onChange={e=>{ setter(e.target.value); setPayWarning(''); }} placeholder="0" style={inputStyle} />
                    </div>
                  ))}
                </div>
                {payWarning && (
                  <div style={{ marginTop:8, padding:'6px 10px', borderRadius:7, background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', fontSize:'0.78rem', fontWeight:600 }}>
                    {payWarning}
                  </div>
                )}
              </div>
            )}
            <div style={{ gridColumn:'1/-1' }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any additional notes…" style={{ ...inputStyle, minHeight:56, resize:'vertical' }} />
            </div>
          </div>
          <div style={{ background:capBg, border:`1px solid ${capColor}`, borderRadius:8, padding:'8px 12px', fontSize:'0.8rem', color:capColor, fontWeight:600, marginTop:12 }}>
            Selected tables fit {capRange.min}–{capRange.max} pax · You entered {totalPax} pax
          </div>

          {/* Optional guest list — collapsed by default; kept mounted (display
              toggle) so the in-memory list survives collapse; saved after the
              walk-in booking row is created. */}
          <div style={{ marginTop:14, borderTop:'1px solid var(--color-border)', paddingTop:12 }}>
            <button
              type="button"
              onClick={()=>setGuestsOpen(v=>!v)}
              aria-expanded={guestsOpen}
              style={{ display:'inline-flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:'2px 0', fontSize:'0.82rem', fontWeight:700, color:'#2a3a5a' }}
            >
              <ChevronRight size={15} strokeWidth={2} aria-hidden="true" style={{ transform: guestsOpen ? 'rotate(90deg)' : 'none', transition:'transform 0.15s' }} />
              <Users size={15} strokeWidth={1.75} aria-hidden="true" />
              Guest list (optional)
            </button>
            <div style={{ display: guestsOpen ? 'block' : 'none', marginTop:12, background:'#fff', border:'1px solid var(--color-border)', borderRadius:10, padding:'14px 16px' }}>
              <GuestListEditor
                deferred
                primaryName={bookingName}
                adults={adults}
                children={children}
                onChange={setGuestSnapshot}
              />
            </div>
          </div>
        </div>
        {ratesStatus === 'error' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', fontSize:'0.78rem', color:'#dc2626', marginTop:12, fontWeight:600 }}>
            <AlertTriangle size={15} strokeWidth={2} aria-hidden="true" style={{ flexShrink:0 }} />
            <span style={{ flex:1 }}>Price couldn't be loaded for {cruise} · {deck}. This walk-in can't be saved until pricing loads (FOC walk-ins can).</span>
            <button type="button" className="btn btn-outline btn-sm" onClick={retryRates} style={{ flexShrink:0 }}>Retry</button>
          </div>
        )}
        {error && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', fontSize:'0.78rem', color:'#dc2626', marginTop:12 }}>
            {error}
          </div>
        )}
        <div style={{ display:'flex', gap:10, marginTop:16, flexShrink:0 }}>
          <button className="btn btn-gold" onClick={handleSave} disabled={saving||!bookingName.trim()||!adults||(ratesStatus!=='ok'&&paymentStatus!=='FOC')}>
            {saving ? 'Saving…' : '+ Add Walk-in Booking'}
          </button>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
      {confirmNonConfirmed && (
        <div className="ss-modal-backdrop" onClick={e=>{ e.stopPropagation(); setConfirmNonConfirmed(false); }}>
          <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:400 }}>
            <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:8 }}>Booking Not Confirmed</h3>
            <p style={{ fontSize:'0.85rem', color:'var(--color-ink-muted)', marginBottom:16 }}>
              This walk-in booking is not Confirmed (status: {paymentStatus}). The selected table will be assigned. Proceed?
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-gold" onClick={()=>{ setConfirmNonConfirmed(false); doSave(); }}>Confirm & Assign</button>
              <button className="btn btn-outline" onClick={()=>setConfirmNonConfirmed(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SeatingSheet (main) ──────────────────────────────────────────────────────

// Online-hold block: the public-site trigger raises TABLE_HELD (Postgres errcode
// P0001) when a manager tries to assign a table that's being booked online.
const HELD_MSG = 'This table is being booked online right now. Try again in a few minutes.';
const isHeldError = (error) => !!error && error.code === 'P0001' && /TABLE_HELD/i.test(error.message || '');

// Staff table block: the table_blocks triggers raise TABLE_BLOCKED (same P0001
// pattern as TABLE_HELD) if an insert touches a blocked table on a blocked date.
const isBlockedError = (error) => !!error && error.code === 'P0001' && /TABLE_BLOCKED/i.test(error.message || '');
// block rows carry real YYYY-MM-DD date columns; toDisplayDate renders them.
const blockRangeLabel = b => b.start_date === b.end_date
  ? toDisplayDate(b.start_date)
  : `${toDisplayDate(b.start_date)} to ${toDisplayDate(b.end_date)}`;
const blockedMsg = b => `This table is blocked ${blockRangeLabel(b)}${b.cruise_type ? '' : ' (all cruises)'}${b.reason ? ` — ${b.reason}` : ''}.`;

export default function SeatingSheet({ date, initialCruise, highlightBookingId, onNavigate, onStateChange }) {
  const { settings: cruiseSettings } = useCruiseSettings();

  const [inputDate, setInputDate] = useState(() => {
    // Explicit navigation from another screen (Daily Tracker / URL) always wins.
    const fromNav = toInputDate(date);
    if (fromNav) return fromNav;
    // Persisted date: keep today or future (forward planning); snap PAST dates
    // to today so a fresh open days later doesn't show a stale sheet. The
    // persist effect below then rewrites ag_seating_date to the snapped value.
    const persisted = localStorage.getItem('ag_seating_date') || '';
    const today = todayInput();
    return (/^\d{4}-\d{2}-\d{2}$/.test(persisted) && persisted >= today) ? persisted : today;
  });
  const [cruise, setCruise] = useState(() =>
    initialCruise || localStorage.getItem('ag_seating_cruise') || 'Sunset Cruise'
  );

  const [tableInventory, setTableInventory] = useState([]);
  const [overrides,      setOverrides]      = useState([]);
  const [assignments,    setAssignments]    = useState([]);
  const [boardedStatus,  setBoardedStatus]  = useState([]);
  const [extraTables,    setExtraTables]    = useState([]);
  const [bookings,       setBookings]       = useState([]);
  const [holds,          setHolds]          = useState([]);  // active online holds (read-only)
  const [closures,       setClosures]       = useState([]);  // online_booking_closures for this cruise+date (read-only)
  const [loading,        setLoading]        = useState(false);

  const [assigningBooking, setAssigningBooking] = useState(null);
  const [assignWarning,    setAssignWarning]    = useState('');

  const [overrideModal, setOverrideModal] = useState(null);
  const [extraModal,    setExtraModal]    = useState(false);
  const [autoPreview,   setAutoPreview]   = useState(null);
  const [accepting,     setAccepting]     = useState(false);
  const [tableAction,   setTableAction]   = useState(null);
  const [contextMenu,   setContextMenu]   = useState(null);

  const [doneConfirm,           setDoneConfirm]           = useState(false);
  const [capOverridePrompt,     setCapOverridePrompt]     = useState(null);
  const [overriddenTables,      setOverriddenTables]      = useState(new Set());
  const [localBoardedOverrides, setLocalBoardedOverrides] = useState({});
  const [unassignAllConfirm,    setUnassignAllConfirm]    = useState(false);
  const [clearSuccess,          setClearSuccess]          = useState('');

  // Batch assign: tables selected (not yet written) while assigning a booking.
  // Committed in ONE atomic insert by "Done Assigning"; cancel writes nothing.
  const [pendingTables, setPendingTables] = useState(new Set());
  const [committing,    setCommitting]    = useState(false);
  // Rain reseating: same selection machinery, but targets the MIDDLE deck for
  // an Upper Deck booking and commits is_rain rows (temporary; upper retained).
  const [rainMode,      setRainMode]      = useState(false);
  // Tapping beyond the party's needs (prams, spreading out, broken chair) asks
  // once per group; after one confirm, further extra tables add silently.
  const [extraTablePrompt, setExtraTablePrompt] = useState(null); // { tableName, pax, nTables }
  const [extraConfirmed,   setExtraConfirmed]   = useState(false);

  const [walkInMode,     setWalkInMode]     = useState(false);
  const [walkInSelected, setWalkInSelected] = useState(new Set());
  const [walkInModal,    setWalkInModal]    = useState(false);
  const [walkInPostSave, setWalkInPostSave] = useState(null); // { booking, reference, tableList }
  const [sendTarget, setSendTarget] = useState(null);          // booking to send via WhatsApp

  // Table blocks: rows whose start..end range covers the sheet date, fetched for
  // every cruise and scoped to the active cruise in cruiseBlocks/blockedMap
  // (cruise_type null = all-cruises maintenance block). Block mode mirrors
  // walk-in mode, EXCEPT selection may span decks — one block action covers
  // e.g. an Upper and a Middle table together (deliberate).
  const [blocks,        setBlocks]        = useState([]);
  const [blockMode,     setBlockMode]     = useState(false);
  const [blockSelected, setBlockSelected] = useState(new Set());
  const [blockModal,    setBlockModal]    = useState(false);
  const [blockAction,   setBlockAction]   = useState(null);   // { tableName, block } — remove-block dialog

  // Send-link mode: same-deck multi-select like walk-in, but it may include
  // tables that are not online-bookable and need not be adjacent — staff are
  // choosing deliberately. It still refuses occupied, blocked and held tables.
  const [linkMode,     setLinkMode]     = useState(false);
  const [linkSelected, setLinkSelected] = useState(new Set());
  // The open modal holds a SNAPSHOT of the selection ({ tables, deck }), not
  // the live Set. The moment a link is created its hold comes back through
  // fetchHolds, and the self-heal effect below prunes every held table out of
  // linkSelected — which is correct for the sheet, but it must not be what the
  // modal is mounted on, or the result view (link + WhatsApp outcome) unmounts
  // the instant the hold lands.
  const [linkModal,    setLinkModal]    = useState(null);   // null | { tables: string[], deck }

  // "All links" panel: the Table Links list (every date) as a full-screen view
  // over the sheet. Not a mode — opening it closes whichever selection mode is
  // active (the same way the mode buttons close each other), and closing it
  // leaves the sheet's date, cruise and assignments exactly as they were.
  const [linksOpen,    setLinksOpen]    = useState(false);
  const linksBtnRef = useRef(null);

  const highlightedRef  = useRef(false);
  const warnTimerRef    = useRef(null);
  const assignmentsRef  = useRef([]);
  const displayDate     = toDisplayDate(inputDate);

  useEffect(() => { assignmentsRef.current = assignments; }, [assignments]);

  const showWarn = msg => {
    setAssignWarning(msg);
    clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(()=>setAssignWarning(''), 4000);
  };

  // ── Fetch table inventory once ──────────────────────────────────────────────
  useEffect(() => {
    supabase.from('table_inventory').select('*')
      .then(({ data, error }) => {
        console.log('[table_inventory] data:', data, 'error:', error);
        setTableInventory(data || []);
      });
  }, []);

  // ── Fetch per date/cruise ───────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!displayDate||!cruise) return;
    setLoading(true);
    const [ov,as_,bs,et,bk] = await Promise.all([
      supabase.from('table_overrides').select('*').eq('date',displayDate).eq('cruise_type',cruise),
      supabase.from('seating_assignments').select('*').eq('date',displayDate).eq('cruise_type',cruise),
      supabase.from('boarded_status').select('*').eq('date',displayDate).eq('cruise_type',cruise),
      supabase.from('extra_tables').select('*').eq('date',displayDate).eq('cruise_type',cruise),
      supabase.from('bookings').select('*').eq('date',displayDate).eq('cruise_type',cruise).order('created_at'),
    ]);
    setOverrides(ov.data||[]);
    setAssignments(as_.data||[]);
    setLocalBoardedOverrides({});
    setBoardedStatus(bs.data||[]);
    setExtraTables(et.data||[]);
    setBookings(bk.data||[]);
    setLoading(false);
  }, [displayDate, cruise]);

  useEffect(()=>{ fetchData(); }, [fetchData]);

  // ── Online holds — READ ONLY. Active, unexpired holds for this cruise+date.
  // (Both decks fetched; matched to a table's deck via getTableDeck below.) The
  // internal app never writes to or deletes from booking_holds.
  const fetchHolds = useCallback(async () => {
    if (!displayDate||!cruise) return;
    const { data, error } = await supabase
      .from('booking_holds')
      .select('table_names, deck, expires_at, source')   // source: 'online' | 'table_link'
      .eq('status','active')
      .eq('cruise_type',cruise)
      .eq('date',displayDate)                       // dates are DD-MM-YYYY text
      .gt('expires_at', new Date().toISOString());  // expires_at > now()
    if (!error) setHolds(data||[]);
  }, [displayDate, cruise]);

  const openLinks = () => {
    setWalkInMode(false); setWalkInSelected(new Set());
    setBlockMode(false);  setBlockSelected(new Set());
    setLinkMode(false);   setLinkSelected(new Set());
    setAssigningBooking(null); setAssignWarning('');
    setLinksOpen(true);
  };
  const closeLinks = useCallback(() => {
    setLinksOpen(false);
    fetchHolds();                               // a cancel or reissue in the panel shows on the sheet at once
    requestAnimationFrame(() => linksBtnRef.current?.focus());
  }, [fetchHolds]);

  // Initial load + reload when date/cruise changes.
  useEffect(()=>{ fetchHolds(); }, [fetchHolds]);

  // Keep holds live: re-query (holds only) every 30s and when the window refocuses,
  // so expired holds clear and new ones appear without reloading the whole sheet.
  useEffect(()=>{
    const id = setInterval(fetchHolds, 30000);
    const onFocus = () => fetchHolds();
    window.addEventListener('focus', onFocus);
    return ()=>{ clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [fetchHolds]);

  // ── Online booking closures — READ ONLY. Rows for THIS cruise+date.
  // The closures manager (Cruises & Pricing) owns writes; the sheet only reads.
  // Resolve the current cruise NAME → its cruise_code via cruise_settings (don't
  // assume the sheet holds the code), then match cruise_code + date (both
  // DD-MM-YYYY text → direct match). deck = NULL means the whole cruise is closed.
  const fetchClosures = useCallback(async () => {
    if (!displayDate||!cruise) return;
    const code = cruiseSettings.find(s => s.cruise_name === cruise)?.cruise_code;
    if (!code) { setClosures([]); return; }   // settings not loaded yet / unknown cruise
    const { data, error } = await supabase
      .from('online_booking_closures')
      .select('cruise_code, date, deck, reason')
      .eq('cruise_code', code)
      .eq('date', displayDate);               // dates are DD-MM-YYYY text
    if (!error) setClosures(data||[]);
  }, [displayDate, cruise, cruiseSettings]);

  // Load when cruise/date (or settings) change; refresh on window focus — no polling.
  useEffect(()=>{ fetchClosures(); }, [fetchClosures]);
  useEffect(()=>{
    const onFocus = () => fetchClosures();
    window.addEventListener('focus', onFocus);
    return ()=>window.removeEventListener('focus', onFocus);
  }, [fetchClosures]);

  // ── Table blocks covering the sheet date. The query is date-only (fetching
  // every cruise's rows keeps the PostgREST filter simple; cruise names contain
  // spaces, which .or() quoting handles poorly) — cruise scoping happens
  // client-side in cruiseBlocks below. start/end are real date columns, so the
  // YYYY-MM-DD inputDate compares directly. Focus refresh, no polling.
  const fetchBlocks = useCallback(async () => {
    if (!inputDate) return;
    const { data, error } = await supabase
      .from('table_blocks')
      .select('id, table_name, start_date, end_date, reason, cruise_type')
      .lte('start_date', inputDate)
      .gte('end_date', inputDate);
    if (!error) setBlocks(data||[]);
  }, [inputDate]);

  useEffect(()=>{ fetchBlocks(); }, [fetchBlocks]);
  useEffect(()=>{
    const onFocus = () => fetchBlocks();
    window.addEventListener('focus', onFocus);
    return ()=>window.removeEventListener('focus', onFocus);
  }, [fetchBlocks]);

  // ── Highlight from Add Inquiry ──────────────────────────────────────────────
  useEffect(()=>{
    if (!highlightBookingId||highlightedRef.current||!bookings.length) return;
    const b = bookings.find(b=>b.id===highlightBookingId);
    if (b) { highlightedRef.current=true; setAssigningBooking(b); }
  }, [bookings, highlightBookingId]);

  // ── Keyboard shortcut ───────────────────────────────────────────────────────
  useEffect(()=>{
    const h = e=>{ if(e.key==='Escape'){ setAssigningBooking(null); setContextMenu(null); setAssignWarning(''); setDoneConfirm(false); setCapOverridePrompt(null); setWalkInMode(false); setWalkInSelected(new Set()); setWalkInModal(false); setWalkInPostSave(null); setBlockMode(false); setBlockSelected(new Set()); setBlockModal(false); setBlockAction(null); setLinkMode(false); setLinkSelected(new Set()); } };
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  }, []);

  useEffect(()=>{
    setDoneConfirm(false); setCapOverridePrompt(null); setPendingTables(new Set());
    setExtraTablePrompt(null); setExtraConfirmed(false);
    if (!assigningBooking) setRainMode(false);
  }, [assigningBooking]);

  // Clear walk-in, block and send-link modes when date or cruise changes
  useEffect(()=>{ setWalkInMode(false); setWalkInSelected(new Set()); setWalkInModal(false); setBlockMode(false); setBlockSelected(new Set()); setBlockModal(false); setLinkMode(false); setLinkSelected(new Set()); setLinkModal(null); }, [inputDate, cruise]);

  // ── Persist date/cruise to localStorage (survives refresh) ──────────────────
  useEffect(()=>{ localStorage.setItem('ag_seating_date', inputDate); }, [inputDate]);
  useEffect(()=>{ localStorage.setItem('ag_seating_cruise', cruise); }, [cruise]);

  // Sync URL when date or cruise changes
  useEffect(() => {
    if (!inputDate || !cruise) return;
    onStateChange?.(toDisplayDate(inputDate), cruise);
  }, [inputDate, cruise]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime sync ───────────────────────────────────────────────────────────
  useEffect(()=>{
    if (!displayDate||!cruise) return;

    // Bookings: update in place, handle cancellation + deletion
    const cleanupCancelledBooking = (bookingId) => {
      const tables = assignmentsRef.current.filter(a=>a.booking_id===bookingId).map(a=>a.table_name);
      setAssignments(prev=>prev.filter(a=>a.booking_id!==bookingId));
      setBoardedStatus(prev=>prev.filter(b=>!tables.includes(b.table_name)));
      setLocalBoardedOverrides(prev=>{ const n={...prev}; tables.forEach(t=>delete n[t]); return n; });
    };

    const bkCh = supabase
      .channel(`ss-bk-${displayDate}-${cruise}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'bookings', filter:`date=eq.${displayDate}` },
        ({ eventType, new: n, old: o }) => {
          if (eventType==='INSERT' && n.cruise_type===cruise) {
            setBookings(prev=>prev.some(b=>b.id===n.id) ? prev : [...prev, n]);
          } else if (eventType==='UPDATE' && n.cruise_type===cruise) {
            setBookings(prev=>prev.map(b=>b.id===n.id ? n : b));
            if (n.payment_status==='Cancelled') cleanupCancelledBooking(n.id);
          } else if (eventType==='DELETE') {
            cleanupCancelledBooking(o.id);
            setBookings(prev=>prev.filter(b=>b.id!==o.id));
          }
        })
      .subscribe();

    // Seating assignments: sync additions/removals from other sessions
    const asCh = supabase
      .channel(`ss-as-${displayDate}-${cruise}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'seating_assignments', filter:`date=eq.${displayDate}` },
        ({ eventType, new: n, old: o }) => {
          const rec = n || o;
          if (rec?.cruise_type !== cruise) return;
          if (eventType==='INSERT') {
            setAssignments(prev=>prev.some(a=>a.id===n.id) ? prev : [...prev, n]);
          } else if (eventType==='UPDATE') {
            setAssignments(prev=>prev.map(a=>a.id===n.id ? n : a));
          } else if (eventType==='DELETE') {
            setAssignments(prev=>prev.filter(a=>a.id!==o.id));
          }
        })
      .subscribe();

    const bsCh = supabase
      .channel(`ss-bs-${displayDate}-${cruise}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'boarded_status', filter:`date=eq.${displayDate}` },
        () => {
          supabase.from('boarded_status').select('*').eq('date',displayDate).eq('cruise_type',cruise)
            .then(({ data }) => { if (data) setBoardedStatus(data); });
        })
      .subscribe();

    return ()=>{ supabase.removeChannel(bkCh); supabase.removeChannel(asCh); supabase.removeChannel(bsCh); };
  }, [displayDate, cruise]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if (!contextMenu) return;
    const h=()=>setContextMenu(null);
    window.addEventListener('click',h);
    return ()=>window.removeEventListener('click',h);
  }, [contextMenu]);

  // ── Derived maps ────────────────────────────────────────────────────────────
  const overrideMap = useMemo(()=>{ const m={}; overrides.forEach(o=>{ m[o.table_name]=o; }); return m; }, [overrides]);
  const assignmentMap = useMemo(()=>{ const m={}; assignments.forEach(a=>{ m[a.table_name]=a; }); return m; }, [assignments]);
  // Held tables keyed by `${deck}__${name}` so a held table is matched to its own deck.
  const heldSet = useMemo(()=>{
    const s = new Set();
    holds.forEach(h => (h.table_names||[]).forEach(n => s.add(`${h.deck}__${n}`)));
    return s;
  }, [holds]);
  // The subset held by a staff-issued table link (booking_holds.source), so the
  // cell can read "Link" rather than "Online".
  const linkHeldSet = useMemo(()=>{
    const s = new Set();
    holds.forEach(h => { if (h.source === 'table_link') (h.table_names||[]).forEach(n => s.add(`${h.deck}__${n}`)); });
    return s;
  }, [holds]);
  // Blocked tables (staff table_blocks) for the sheet date and ACTIVE cruise —
  // a row applies when its cruise_type matches the sheet's cruise or is null
  // (all-cruises maintenance block). Table names are unique across decks, so no
  // deck keying is needed (unlike holds). Where a table carries both an
  // all-cruises and a cruise-specific block, the cruise-specific one wins the
  // map slot so its reason is the one displayed.
  const cruiseBlocks = useMemo(()=>blocks.filter(b => !b.cruise_type || b.cruise_type === cruise), [blocks, cruise]);
  const blockedMap = useMemo(()=>{
    const m={};
    cruiseBlocks.forEach(b=>{ if (!m[b.table_name] || b.cruise_type) m[b.table_name]=b; });
    return m;
  }, [cruiseBlocks]);
  // Closures: a row with deck=NULL closes the WHOLE cruise that date; a row with a
  // deck name (matching table_inventory, e.g. "Middle Deck") closes only that deck.
  const wholeCruiseClosure = useMemo(()=>closures.find(c => !c.deck) || null, [closures]);
  const closedDecks = useMemo(()=>{
    const m = {};                                   // deck name → reason
    closures.forEach(c => { if (c.deck) m[c.deck] = c.reason || ''; });
    return m;
  }, [closures]);
  // Live table names on closed decks — fed to auto-assign as "unavailable".
  const closedDeckTableNames = useMemo(()=>{
    if (wholeCruiseClosure) return [];              // handled as a hard refuse instead
    if (Object.keys(closedDecks).length === 0) return [];
    const names = [];
    tableInventory.forEach(t => { if (closedDecks[t.deck] !== undefined) names.push(t.table_name||t.name); });
    extraTables.forEach(t => { if (closedDecks[t.deck] !== undefined) names.push(t.table_name||t.name); });
    return names;
  }, [wholeCruiseClosure, closedDecks, tableInventory, extraTables]);
  const boardedMap = useMemo(()=>{
    const m={};
    boardedStatus.forEach(b=>{ if(b.table_name) m[b.table_name]=b.boarded; });
    return { ...m, ...localBoardedOverrides };
  }, [boardedStatus, localBoardedOverrides]);
  // bookingMap includes ALL bookings (even cancelled) so assigned rows still show a name if cleanup lags
  const bookingMap = useMemo(()=>{ const m={}; bookings.forEach(b=>{ m[b.id]=b; }); return m; }, [bookings]);
  // activeBookings excludes cancelled — used for panel, capacity counts, and stats
  const activeBookings = useMemo(()=>bookings.filter(b=>b.payment_status!=='Cancelled'), [bookings]);
  const assignedBookingIds = useMemo(()=>new Set(assignments.map(a=>a.booking_id)), [assignments]);
  const bookingTableCount  = useMemo(()=>{ const m={}; assignments.forEach(a=>{ m[a.booking_id]=(m[a.booking_id]||0)+1; }); return m; }, [assignments]);
  const unassignedBookings = useMemo(()=>activeBookings.filter(b=>!assignedBookingIds.has(b.id)), [activeBookings, assignedBookingIds]);

  // ── Summary stats (Adults + Children only, cancelled excluded) ──────────────
  const totalPax    = useMemo(()=>activeBookings.reduce((s,b)=>s+paxVisible(b),0), [activeBookings]);
  const assignedPax = useMemo(()=>activeBookings.filter(b=>assignedBookingIds.has(b.id)).reduce((s,b)=>s+paxVisible(b),0), [activeBookings, assignedBookingIds]);
  const boardedPax  = useMemo(()=>activeBookings.filter(b=>{
    const tabs = assignments.filter(a=>a.booking_id===b.id);
    return tabs.some(a=>boardedMap[a.table_name]);
  }).reduce((s,b)=>s+paxVisible(b),0), [activeBookings, assignments, boardedMap]);

  // ── getCap ──────────────────────────────────────────────────────────────────
  const getCap = useCallback(tableName=>{
    if (overrideMap[tableName]) return { min:overrideMap[tableName].min_capacity, max:overrideMap[tableName].max_capacity, overridden:true };
    // Support both 'name' and 'table_name' column — whichever the DB uses
    const t = tableInventory.find(t=>(t.table_name||t.name)===tableName)
           || extraTables.find(t=>(t.table_name||t.name)===tableName);
    if (!t && tableInventory.length === 0) {
      console.warn('[getCap] tableInventory is empty — data may not have loaded yet');
    }
    return t ? { min:t.min_capacity, max:t.max_capacity, overridden:false } : null;
  }, [overrideMap, tableInventory, extraTables]);

  // ── Rain maps: booking_id → its rain tables / its own (real) tables ────────
  const rainTablesByBooking = useMemo(()=>{
    const m = {};
    assignments.forEach(a=>{ if (a.is_rain) (m[a.booking_id] = m[a.booking_id] || []).push(a.table_name); });
    return m;
  }, [assignments]);
  const ownTablesByBooking = useMemo(()=>{
    const m = {};
    assignments.forEach(a=>{ if (!a.is_rain) (m[a.booking_id] = m[a.booking_id] || []).push(a.table_name); });
    return m;
  }, [assignments]);

  // ── Progress ────────────────────────────────────────────────────────────────
  // Rain rows never count toward a booking's PRIMARY coverage: they are
  // temporary duplicates of the same party, not extra seated capacity.
  const seatedPax = useMemo(()=>{
    if (!assigningBooking) return 0;
    const capSum = assignments.filter(a=>a.booking_id===assigningBooking.id && !a.is_rain)
      .reduce((s,a)=>{ const c=getCap(a.table_name); return s+(c?.max||0); }, 0);
    return Math.min(capSum, paxCount(assigningBooking));
  }, [assigningBooking, assignments, getCap]);

  // ── Highlighted (suitable) tables ──────────────────────────────────────────
  const highlightedTables = useMemo(()=>{
    if (!assigningBooking) return new Set();
    const pax = paxCount(assigningBooking);
    // Rain reseating always targets the Middle Deck regardless of the booking's own deck.
    const deck = rainMode ? 'Middle Deck' : assigningBooking.deck;
    const deckTables = [
      ...(deck==='Upper Deck' ? ALL_UPPER : [...MIDDLE_LEFT, ...MIDDLE_RIGHT]),
      ...extraTables.filter(t=>t.deck===deck).map(t=>t.table_name||t.name),
    ];
    const canFitSingle = deckTables.some(n=>{ const c=getCap(n); return c&&pax<=c.max; });
    if (!canFitSingle) return new Set();
    const result = new Set();
    deckTables.forEach(n=>{ const c=getCap(n); if(c&&!assignmentMap[n]&&!blockedMap[n]&&pax>=c.min&&pax<=c.max) result.add(n); });
    return result;
  }, [assigningBooking, getCap, assignmentMap, blockedMap, extraTables, rainMode]);

  // ── getTableDeck ─────────────────────────────────────────────────────────────
  const getTableDeck = useCallback(tableName=>{
    const inv = tableInventory.find(t=>t.name===tableName);
    if (inv) return inv.deck;
    const ext = extraTables.find(t=>(t.table_name||t.name)===tableName);
    if (ext) return ext.deck;
    return (tableName.startsWith('L')||tableName.startsWith('R')) ? 'Middle Deck' : 'Upper Deck';
  }, [tableInventory, extraTables]);

  // ── getTableClosure ──────────────────────────────────────────────────────────
  // Closed if the whole cruise is closed that date, OR the table's deck is closed.
  // Whole-cruise wins. (Callers treat "closed" with priority over "held".)
  const getTableClosure = useCallback(tableName=>{
    if (wholeCruiseClosure) return { scope:'cruise', label:cruise, reason:wholeCruiseClosure.reason||'' };
    const deck = getTableDeck(tableName);
    if (closedDecks[deck] !== undefined) return { scope:'deck', label:deck, reason:closedDecks[deck] };
    return null;
  }, [wholeCruiseClosure, closedDecks, getTableDeck, cruise]);

  // "This [cruise/deck] is closed on [date] — [reason]."
  const closedMsg = useCallback(closure =>
    `This ${closure.label} is closed on ${displayDate}${closure.reason ? ` — ${closure.reason}` : ''}.`,
  [displayDate]);

  // ── Batch selection capacity (committed + selected, capped at party size) ──
  const pendingCapSum = useMemo(()=>
    [...pendingTables].reduce((s,t)=>{ const c=getCap(t); return s+(c?.max||0); }, 0),
  [pendingTables, getCap]);
  // Rain mode measures the selection alone against the party (the retained
  // upper table doesn't shelter anyone downstairs).
  const coveredPax = useMemo(()=>
    assigningBooking
      ? Math.min((rainMode ? 0 : seatedPax) + pendingCapSum, paxCount(assigningBooking))
      : 0,
  [assigningBooking, seatedPax, pendingCapSum, rainMode]);

  // Keep the pending selection valid: drop a selected table the moment it becomes
  // assigned (another device committed it — realtime feed) or held by an online
  // booking. Paired with the atomic commit below, a failed commit self-heals:
  // the conflicting table leaves the selection and staff just press Done again.
  useEffect(()=>{
    setPendingTables(prev=>{
      if (prev.size===0) return prev;
      const next = new Set([...prev].filter(t=>!assignmentMap[t]));
      return next.size===prev.size ? prev : next;
    });
  }, [assignmentMap]);
  useEffect(()=>{
    const pruneHeld = prev=>{
      if (prev.size===0) return prev;
      const next = new Set([...prev].filter(t=>!heldSet.has(`${getTableDeck(t)}__${t}`)));
      return next.size===prev.size ? prev : next;
    };
    setPendingTables(pruneHeld);
    setLinkSelected(pruneHeld);
  }, [heldSet, getTableDeck]);
  // A send-link selection also drops a table the moment it gets seated elsewhere.
  useEffect(()=>{
    setLinkSelected(prev=>{
      if (prev.size===0) return prev;
      const next = new Set([...prev].filter(t=>!assignmentMap[t]));
      return next.size===prev.size ? prev : next;
    });
  }, [assignmentMap]);
  // Same self-heal for blocks: drop a selected table the moment another device
  // blocks it (assign + walk-in selections; the block backstop trigger makes a
  // stale selection fail atomically anyway, this just spares the retry).
  useEffect(()=>{
    const prune = prev=>{
      if (prev.size===0) return prev;
      const next = new Set([...prev].filter(t=>!blockedMap[t]));
      return next.size===prev.size ? prev : next;
    };
    setPendingTables(prune);
    setWalkInSelected(prune);
    setLinkSelected(prune);
  }, [blockedMap]);

  // ── Assignment actions ──────────────────────────────────────────────────────
  // Batch commit — ONE atomic insert for every selected table. An array insert
  // is a single PostgREST request and a single SQL statement: if any row fails
  // (unique slot index — table taken; held-table trigger), Postgres aborts the
  // whole statement and ZERO rows land. Never a half-seated group. On failure
  // the selection is kept (minus the conflicting table, via the prune effects)
  // so staff adjust and press Done again.
  const commitPendingTables = async () => {
    if (!assigningBooking || pendingTables.size===0 || committing) return;
    const alreadyAssigned = assignments.some(a=>a.booking_id===assigningBooking.id);
    const rows = [...pendingTables].map((t,i)=>({
      booking_id: assigningBooking.id, table_name: t,
      date: displayDate, cruise_type: cruise,
      // Rain rows are always continuations (pax numbers stay on the real table)
      // and carry the explicit is_rain marker for display + targeted reversal.
      is_continuation: rainMode || alreadyAssigned || i>0,
      ...(rainMode ? { is_rain: true } : {}),
    }));
    setCommitting(true);
    const { error } = await supabase.from('seating_assignments').insert(rows);
    setCommitting(false);
    if (error) {
      if (rainMode && (error.code === 'PGRST204' || /is_rain/i.test(error.message || ''))) {
        showWarn('Rain reseating needs a database update — run db/rain_reseating.sql in the Supabase SQL editor, then try again.');
        return;
      }
      if (isBlockedError(error)) {
        showWarn('A selected table is blocked for this date — nothing was saved. It will leave your selection; press Done Assigning again for the rest.');
        fetchBlocks();
      } else if (isHeldError(error)) {
        showWarn('A selected table is being booked online right now — nothing was saved. It will leave your selection; press Done Assigning again for the rest.');
        fetchHolds();
      } else if (error.code === '23505') {
        showWarn('A selected table was just taken by someone else — nothing was saved. It will leave your selection; press Done Assigning again for the rest.');
        fetchData();
      } else {
        showWarn('Could not save the assignment — nothing was saved. Please press Done Assigning again.');
      }
      return;
    }
    setPendingTables(new Set());
    setAssigningBooking(null);
    setAssignWarning('');
    setDoneConfirm(false);
    fetchData();
  };

  // Done Assigning: combined-minimum check (first commit only), then partial-
  // coverage confirm, then the atomic commit. With nothing selected it just
  // exits the mode (old behaviour, incl. the partial-seating confirm).
  const handleDoneAssigning = (opts={}) => {
    if (!assigningBooking || committing) return;
    // Rain mode: no combined-minimum prompt and no partial-coverage confirm —
    // spreading out downstairs (more OR fewer covered pax) is the point.
    const { skipMin=rainMode, skipPartial=rainMode } = opts;
    const pax = paxCount(assigningBooking);
    if (pendingTables.size===0) {
      if (!skipPartial && seatedPax>0 && seatedPax<pax) { setDoneConfirm(true); return; }
      setAssigningBooking(null); setAssignWarning(''); setDoneConfirm(false);
      return;
    }
    const alreadyAssigned = assignments.some(a=>a.booking_id===assigningBooking.id);
    if (!skipMin && !alreadyAssigned) {
      const minSum = [...pendingTables].reduce((s,t)=>{ const c=getCap(t); return s+(c?.min||0); }, 0);
      if (pax < minSum) {
        setCapOverridePrompt({ message:`Selected tables need at least ${minSum} pax combined — this group has ${pax}. Override and assign anyway?` });
        return;
      }
    }
    if (!skipPartial && coveredPax < pax) { setDoneConfirm(true); return; }
    commitPendingTables();
  };

  const unassignTable = async tableName => {
    await supabase.from('seating_assignments').delete()
      .eq('table_name',tableName).eq('date',displayDate).eq('cruise_type',cruise);
    fetchData();
  };

  const unassignAllForBooking = async bookingId => {
    await supabase.from('seating_assignments').delete()
      .eq('booking_id',bookingId).eq('date',displayDate).eq('cruise_type',cruise);
    fetchData();
  };

  // End rain seating for one booking: delete ONLY its is_rain rows (the real
  // upper allocation is untouched) and clear boarded status on the freed
  // middle tables so the next party there doesn't inherit a green tick.
  const endRainSeating = async bookingId => {
    const rainTables = (rainTablesByBooking[bookingId] || []);
    const { error } = await supabase.from('seating_assignments').delete()
      .eq('booking_id',bookingId).eq('date',displayDate).eq('cruise_type',cruise)
      .eq('is_rain',true);
    if (error) { showWarn('Could not end rain seating — please try again.'); return; }
    if (rainTables.length) {
      await supabase.from('boarded_status').delete()
        .eq('date',displayDate).eq('cruise_type',cruise)
        .in('table_name',rainTables);
      removeFromOverridden(rainTables);
      setLocalBoardedOverrides(prev=>{ const n={...prev}; rainTables.forEach(t=>delete n[t]); return n; });
    }
    fetchData();
  };

  const unassignAll = async () => {
    await Promise.all([
      supabase.from('seating_assignments').delete().eq('date',displayDate).eq('cruise_type',cruise),
      supabase.from('boarded_status').delete().eq('date',displayDate).eq('cruise_type',cruise),
    ]);
    setOverriddenTables(new Set());
    setLocalBoardedOverrides({});
    setUnassignAllConfirm(false);
    await fetchData();
    setClearSuccess(`All assignments cleared for ${cruise} on ${formatDateWords(inputDate)}`);
    setTimeout(()=>setClearSuccess(''), 5000);
  };

  const toggleBoarded = useCallback(async (tableName, bookingId) => {
    const newVal = !boardedMap[tableName];
    const bookingTables = assignments
      .filter(a => a.booking_id === bookingId)
      .map(a => a.table_name);

    // Optimistic update — local overrides take priority over DB data
    const patch = {};
    bookingTables.forEach(t => { patch[t] = newVal; });
    setLocalBoardedOverrides(prev => ({ ...prev, ...patch }));

    // Persist each table without refetching (local state is source of truth until next full load)
    for (const t of bookingTables) {
      const { error } = await supabase.from('boarded_status').upsert(
        { table_name: t, date: displayDate, cruise_type: cruise, boarded: newVal, boarded_at: new Date().toISOString() },
        { onConflict: 'date,cruise_type,table_name' }
      );
      if (error) console.error('Boarded upsert error:', error, 'table:', t);
    }
  }, [boardedMap, assignments, displayDate, cruise]);

  const addExtraTable = async data => {
    const { error } = await supabase.from('extra_tables').insert({ ...data, date:displayDate, cruise_type:cruise });
    if (error) throw new Error(error.message||'Failed to save extra table');
    setExtraModal(false);
    await fetchData();
  };

  const saveOverride = async (minCap, maxCap) => {
    const { tableName } = overrideModal;
    const existing = overrides.find(o=>o.table_name===tableName);
    if (existing) {
      await supabase.from('table_overrides').update({ min_capacity:minCap, max_capacity:maxCap }).eq('id',existing.id);
    } else {
      await supabase.from('table_overrides').insert({ table_name:tableName, date:displayDate, cruise_type:cruise, min_capacity:minCap, max_capacity:maxCap });
    }
    setOverrideModal(null);
    fetchData();
  };

  // ── Cell click handler ──────────────────────────────────────────────────────
  const handleCellClick = tableName => {
    const assignment = assignmentMap[tableName];
    // A closed table (online booking closed for this cruise/deck+date) and a held
    // table (online "booking in progress") are never valid seating targets.
    // Closed wins over held.
    const closure = getTableClosure(tableName);
    const isHeldTable = heldSet.has(`${getTableDeck(tableName)}__${tableName}`);
    const block = blockedMap[tableName];

    if (blockMode) {
      // Cross-deck selection is deliberate: one block action may cover tables
      // on both decks. Assigned tables are selectable too — a block never
      // touches existing seating, it only refuses NEW bookings in its range.
      if (block) { showWarn(`${tableName} is already blocked ${blockRangeLabel(block)}${block.reason ? ` — ${block.reason}` : ''}.`); return; }
      setBlockSelected(prev => { const next = new Set(prev); if (next.has(tableName)) next.delete(tableName); else next.add(tableName); return next; });
      return;
    }

    if (linkMode) {
      // Occupied, blocked and held tables are never offered. Closed decks and
      // non-online-bookable tables ARE allowed — a link is staff selling by
      // hand — and the selection need not be adjacent. One deck per link.
      if (assignment) { showWarn('Only empty tables can go in a booking link.'); return; }
      if (block) { showWarn(blockedMsg(block)); return; }
      if (isHeldTable) { showWarn(linkHeldSet.has(`${getTableDeck(tableName)}__${tableName}`) ? 'This table is already held by a booking link.' : HELD_MSG); return; }
      const tDeck = getTableDeck(tableName);
      if (linkSelected.size > 0) {
        const firstDeck = getTableDeck([...linkSelected][0]);
        if (firstDeck !== tDeck) { showWarn(`Tables must be on the same deck. Already selected ${firstDeck} tables.`); return; }
      }
      setLinkSelected(prev => { const next = new Set(prev); if (next.has(tableName)) next.delete(tableName); else next.add(tableName); return next; });
      return;
    }

    if (walkInMode) {
      if (assignment) { showWarn('Only empty tables can be selected for walk-in.'); return; }
      if (block) { showWarn(blockedMsg(block)); return; }
      if (closure) { showWarn(closedMsg(closure)); return; }
      if (isHeldTable) { showWarn(HELD_MSG); return; }
      const tDeck = getTableDeck(tableName);
      if (walkInSelected.size > 0) {
        const firstDeck = getTableDeck([...walkInSelected][0]);
        if (firstDeck !== tDeck) { showWarn(`Tables must be on the same deck. Already selected ${firstDeck} tables.`); return; }
      }
      setWalkInSelected(prev => { const next = new Set(prev); if (next.has(tableName)) next.delete(tableName); else next.add(tableName); return next; });
      return;
    }

    if (assigningBooking) {
      if (committing) return;
      if (assignment) return;
      // Toggle off first — a selected table can always be deselected.
      if (pendingTables.has(tableName)) {
        setPendingTables(prev=>{ const n=new Set(prev); n.delete(tableName); return n; });
        return;
      }
      if (block) { showWarn(blockedMsg(block)); return; }
      if (closure) { showWarn(closedMsg(closure)); return; }
      if (isHeldTable) { showWarn(HELD_MSG); return; }

      const tableDeck  = getTableDeck(tableName);
      if (rainMode) {
        // Rain reseating targets ONLY the Middle Deck; no capacity prompts —
        // a group spreading out downstairs may take more space than upstairs.
        if (tableDeck !== 'Middle Deck') {
          showWarn('Rain reseating goes to the Middle Deck — select a Middle Deck row.');
          return;
        }
        setPendingTables(prev=>new Set(prev).add(tableName));
        return;
      }

      const bookingDeck = assigningBooking.deck;
      if (bookingDeck && tableDeck!==bookingDeck) {
        showWarn(`This booking is for ${bookingDeck}. Assign to a ${bookingDeck} row.`);
        return;
      }

      const pax = paxCount(assigningBooking);
      if (coveredPax>=pax && pax>0 && !extraConfirmed) {
        const nTables = assignments.filter(a=>a.booking_id===assigningBooking.id).length + pendingTables.size;
        setExtraTablePrompt({ tableName, pax, nTables });
        return;
      }

      setPendingTables(prev=>new Set(prev).add(tableName));
      return;
    }

    if (assignment) {
      const booking = bookingMap[assignment.booking_id];
      setTableAction({ tableName, assignment, booking });
      return;
    }

    // Empty blocked table: offer to remove the block (deletes the whole
    // date-range row for this table, not just the sheet date).
    if (block) setBlockAction({ tableName, block });
  };

  const handleContextMenu = (e, tableName) => {
    e.preventDefault();
    setContextMenu({ x:e.clientX, y:e.clientY, tableName });
  };

  // ── Auto-assign ─────────────────────────────────────────────────────────────
  const handleAutoAssign = () => {
    // Whole cruise closed → nothing can be auto-assigned that date.
    if (wholeCruiseClosure) { showWarn(closedMsg({ scope:'cruise', label:cruise, reason:wholeCruiseClosure.reason||'' })); return; }
    // Closed-deck and blocked tables are passed as "already taken" so auto-assign
    // never targets them; affected bookings simply fall through as unseated.
    const unavailable = [...closedDeckTableNames, ...cruiseBlocks.map(b => b.table_name)];
    const syntheticAssignments = unavailable.length
      ? [...assignments, ...unavailable.map(n => ({ table_name:n }))]
      : assignments;
    setAutoPreview(computeAutoAssign({ unassignedBookings, tableInventory, extraTables, overrides, assignments: syntheticAssignments, cruise }));
  };

  const acceptAutoAssign = async () => {
    if (!autoPreview) return;
    setAccepting(true);
    let heldHit = false, closedHit = false, blockedHit = false;
    for (const p of autoPreview.filter(p=>p.success)) {
      for (let i=0;i<p.tables.length;i++) {
        // Backstop: skip a table that became closed or blocked since the preview was built.
        if (getTableClosure(p.tables[i])) { closedHit = true; continue; }
        if (blockedMap[p.tables[i]]) { blockedHit = true; continue; }
        const { error } = await supabase.from('seating_assignments').insert({
          booking_id:p.booking.id, table_name:p.tables[i],
          date:displayDate, cruise_type:cruise, is_continuation:i>0,
        });
        if (isHeldError(error)) heldHit = true;
        else if (isBlockedError(error)) blockedHit = true;
      }
    }
    setAccepting(false);
    setAutoPreview(null);
    if (closedHit) { showWarn('Some tables are now closed for online booking — left unassigned.'); fetchClosures(); }
    if (blockedHit) { showWarn('Some tables are now blocked — left unassigned.'); fetchBlocks(); }
    if (heldHit) { showWarn(HELD_MSG); fetchHolds(); }
    fetchData();
  };

  // ── Table action handlers ───────────────────────────────────────────────────
  const removeFromOverridden = tableNames => {
    setOverriddenTables(prev => {
      const next = new Set(prev);
      tableNames.forEach(t => next.delete(t));
      return next;
    });
  };

  const handleReassign = () => {
    const { tableName, booking } = tableAction;
    setTableAction(null);
    removeFromOverridden([tableName]);
    unassignTable(tableName).then(()=>setAssigningBooking(booking));
  };

  const handleUnassign = async all => {
    const { tableName, assignment } = tableAction;
    setTableAction(null);
    if (all) {
      const bookingTables = assignments.filter(a=>a.booking_id===assignment.booking_id).map(a=>a.table_name);
      removeFromOverridden(bookingTables);
      await unassignAllForBooking(assignment.booking_id);
    } else {
      removeFromOverridden([tableName]);
      await unassignTable(tableName);
    }
  };

  const shiftDay = n => {
    const d = new Date(inputDate); d.setDate(d.getDate()+n);
    setInputDate(d.toISOString().slice(0,10));
    setAssigningBooking(null); setAssignWarning('');
  };

  const handleWalkInSave = async (formData) => {
    // Backstop for a closure or block added between table selection and submit:
    // refuse the whole save (walk-in is one booking on one deck) before creating anything.
    const closedSel = [...walkInSelected].map(getTableClosure).find(Boolean);
    if (closedSel) { showWarn(closedMsg(closedSel)); return; }
    const blockedSel = [...walkInSelected].map(t=>blockedMap[t]).find(Boolean);
    if (blockedSel) { showWarn(blockedMsg(blockedSel)); return; }
    const payload = {
      date: displayDate, cruise_type: cruise, deck: walkInDeck,
      booking_name: formData.bookingName, partner_name: formData.partnerName||null,
      partner_id: formData.partnerId || null,
      partner_phone: formData.partnerPhone||null, guest_phone: formData.guestPhone||null,
      adults: formData.adults, children: formData.children,
      infants: formData.infants, tour_leaders: formData.tourLeaders,
      food_non_veg: formData.foodNonVeg || 0, food_veg: formData.foodVeg || 0, food_jain: formData.foodJain || 0,
      total_amount: formData.totalAmount, tac: formData.tac, net_total: formData.netTotal,
      payment_status: formData.paymentStatus||null,
      razorpay_amount: formData.razorpayAmt||0,
      cash_amount: formData.cashAmt||0,
      icici_amount: formData.iciciAmt||0,
      bank_transfer_amount: formData.bankTransferAmt||0,
      gpay_amount: formData.gpayAmt||0,
      notes: formData.notes||null,
    };
    const { data: bk, error: bkErr } = await supabase.from('bookings').insert(payload).select('id').single();
    if (bkErr) throw new Error(bkErr.message);
    const bookingId = bk.id;
    const cs = cruiseSettings.find(s => s.cruise_name === cruise);
    let reference = null;
    if (cs?.cruise_code) {
      reference = await generateBookingReference(bookingId, cs.cruise_code, displayDate).catch(()=>null);
    }
    // Deferred guest list: persist now that the booking id exists. The walk-in
    // booking already succeeded — a guest failure is a non-blocking warning and
    // must NOT throw (that would roll the modal back as if the booking failed).
    if (hasGuestData(formData.guestSnapshot)) {
      const gRes = await persistGuestList(bookingId, {
        primaryName: formData.bookingName,
        primary: formData.guestSnapshot.primary,
        additional: formData.guestSnapshot.additional,
        cap: formData.guestSnapshot.cap,
      });
      if (gRes.error) showWarn(`Walk-in saved, but the guest list could not be saved (${gRes.error}). Add it later from View Bookings.`);
    }
    const tableList = [...walkInSelected];
    const assignedTables = [];
    const heldTables = [];
    let blockedRace = false;
    for (let i = 0; i < tableList.length; i++) {
      const { error: aErr } = await supabase.from('seating_assignments').insert({
        booking_id: bookingId, table_name: tableList[i],
        date: displayDate, cruise_type: cruise, is_continuation: assignedTables.length > 0,
      });
      if (isHeldError(aErr)) heldTables.push(tableList[i]);
      else if (isBlockedError(aErr)) blockedRace = true;
      else if (!aErr) assignedTables.push(tableList[i]);
    }
    // Backstop for the race where a table gets held or blocked between form-open
    // and submit: surface the same friendly message instead of failing silently.
    if (heldTables.length) { showWarn(HELD_MSG); fetchHolds(); }
    if (blockedRace) { showWarn('A selected table was just blocked — left in Unassigned.'); fetchBlocks(); }
    const bookingObj = { id: bookingId, date: displayDate, cruise_type: cruise, deck: walkInDeck, booking_name: formData.bookingName, partner_name: formData.partnerName||null, guest_phone: formData.guestPhone||null, partner_phone: formData.partnerPhone||null, adults: formData.adults, children: formData.children, infants: formData.infants, tour_leaders: formData.tourLeaders, notes: formData.notes||null };
    setWalkInMode(false); setWalkInSelected(new Set()); setWalkInModal(false);
    setWalkInPostSave({ booking: bookingObj, reference, tableList: assignedTables, heldTables });
    await fetchData();
  };

  // ── Block actions ───────────────────────────────────────────────────────────
  // One block action = one table_blocks row per selected table (may span decks).
  // cruise_type carries the sheet's cruise NAME, or null when "Apply to all
  // cruises" was chosen; reason is validated non-empty in the modal (DB NOT NULL).
  const handleBlockSave = async ({ startDate, endDate, reason, allCruises }) => {
    const rows = [...blockSelected].map(t => ({
      table_name: t, start_date: startDate, end_date: endDate, reason,
      cruise_type: allCruises ? null : cruise,
    }));
    const { error } = await supabase.from('table_blocks').insert(rows);
    if (error) throw new Error(error.message || 'Failed to save the block');
    setBlockMode(false); setBlockSelected(new Set()); setBlockModal(false);
    fetchBlocks();
  };

  const removeBlock = async block => {
    const { error } = await supabase.from('table_blocks').delete().eq('id', block.id);
    if (error) { showWarn('Could not remove the block — please try again.'); return; }
    setBlockAction(null);
    fetchBlocks();
  };

  // ── Extra table pairs ───────────────────────────────────────────────────────
  const extraPairs = useMemo(()=>{
    const pairs=[];
    for (let i=0;i<extraTables.length;i+=2) {
      pairs.push([
        extraTables[i]?.table_name||extraTables[i]?.name||null,
        extraTables[i+1]?.table_name||extraTables[i+1]?.name||null,
      ]);
    }
    return pairs;
  }, [extraTables]);

  const activeCruiseSetting = cruiseSettings.find(s => s.cruise_name === cruise);
  const showUpper = activeCruiseSetting ? activeCruiseSetting.has_upper_deck : SHOW_UPPER.includes(cruise);

  const walkInDeck = useMemo(() => {
    if (walkInSelected.size === 0) return null;
    return getTableDeck([...walkInSelected][0]);
  }, [walkInSelected, getTableDeck]);

  const walkInCapRange = useMemo(() => {
    let minT = 0, maxT = 0;
    walkInSelected.forEach(t => { const c = getCap(t); if (c) { minT += c.min; maxT += c.max; } });
    return { min: minT, max: maxT };
  }, [walkInSelected, getCap]);

  const linkDeck = useMemo(() => {
    if (linkSelected.size === 0) return null;
    return getTableDeck([...linkSelected][0]);
  }, [linkSelected, getTableDeck]);

  const linkCapRange = useMemo(() => {
    let minT = 0, maxT = 0;
    linkSelected.forEach(t => { const c = getCap(t); if (c) { minT += c.min; maxT += c.max; } });
    return { min: minT, max: maxT };
  }, [linkSelected, getCap]);

  // ── Row cell renderer ───────────────────────────────────────────────────────
  // isRight=true: thick left border to separate the two halves
  const renderCells = (tableName, isRight = false, invisible = false) => {
    const COL_SEP = '1px solid var(--color-border-subtle)';
    const HALF_SEP = '3px solid var(--color-border-strong)';

    if (!tableName) {
      if (invisible) {
        return Array.from({length:8},(_,i)=>(
          <td key={`inv-${isRight}-${i}`} style={{
            background:'transparent',
            border:'none',
            borderLeft: isRight&&i===0 ? HALF_SEP : undefined,
          }} />
        ));
      }
      return Array.from({length:8},(_,i)=>(
        <td key={`empty-${isRight}-${i}`} style={{
          background:'var(--color-sunken)',
          borderBottom:'1px solid var(--color-border-subtle)',
          borderRight: COL_SEP,
          borderLeft: isRight&&i===0 ? HALF_SEP : undefined,
          padding:'8px 4px',
        }} />
      ));
    }

    const assignment  = assignmentMap[tableName];
    const booking     = assignment ? bookingMap[assignment.booking_id] : null;
    const isBoarded   = !!boardedMap[tableName];
    const cap         = getCap(tableName);
    const isHighlight = highlightedTables.has(tableName);
    const isOverridden = overriddenTables.has(tableName);
    const isWalkInSelected = walkInMode && walkInSelected.has(tableName);
    const isBlockSelected = blockMode && blockSelected.has(tableName);
    const isLinkSelected = linkMode && linkSelected.has(tableName);
    // Batch-assign selection: chosen but not yet committed (gold, brand selection)
    const isPendingSel = !assignment && !!assigningBooking && pendingTables.has(tableName);
    // Blocked (staff table_blocks), closed (online booking closed for this
    // cruise/deck+date) and online hold ("booking in progress") apply only to
    // unassigned tables. Blocked wins over closed wins over held.
    const block = !assignment ? blockedMap[tableName] : null;
    const isBlocked = !!block;
    const closure = !assignment && !isBlocked ? getTableClosure(tableName) : null;
    const isClosed = !!closure;
    const isHeld = !assignment && !isBlocked && !isClosed && heldSet.has(`${getTableDeck(tableName)}__${tableName}`);
    const isLinkHeld = isHeld && linkHeldSet.has(`${getTableDeck(tableName)}__${tableName}`);

    const isRainRow = !!assignment?.is_rain;
    // The booking's own (real) tables carry a subline when rain rows exist.
    const rainTablesOfBooking = assignment && !isRainRow ? (rainTablesByBooking[assignment.booking_id] || []) : [];
    const ownTablesOfBooking  = isRainRow ? (ownTablesByBooking[assignment.booking_id] || []) : [];

    const state = !assignment
      ? (isBlocked ? 'blocked' : isClosed ? 'closed' : isHeld ? 'held' : (assigningBooking ? (isHighlight ? 'suitable' : 'target') : 'empty'))
      : isBoarded ? 'boarded'
      : assignment.is_continuation ? 'do'
      : 'assigned';

    // Rain rows read sky-blue unless boarded (green = onboard wins at the gate).
    const BG = isPendingSel ? (rainMode ? '#DBEFFB' : '#FAEFD4')
      : isWalkInSelected ? '#DBEAFE'
      : isBlockSelected ? '#FECACA'
      : isLinkSelected ? '#D1FAE5'
      : (isRainRow && !isBoarded) ? '#E0F2FE'
      : { empty:'#ffffff', target:'#f8fbff', suitable:'#BBF7D0', assigned:'#FEF3C7', do:'#FEF3C7', boarded:'#DCFCE7', held:'#F3E8FF', closed:'#E2E8F0', blocked:'#FEE2E2' }[state]||'#ffffff';

    const isDO = assignment?.is_continuation;

    // Fix 2 & 5: column separator on every cell, comfortable row padding
    const cell = {
      background: BG,
      borderBottom: '1px solid var(--color-border-subtle)',
      borderRight: COL_SEP,
      padding: '8px 6px',
      fontSize: '0.78rem',
      verticalAlign: 'middle',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    };

    const onClick = ()=>handleCellClick(tableName);
    const onCtx   = e=>handleContextMenu(e,tableName);

    return (
      <>
        {/* Cap — fix 1: show "min–max" format */}
        <td style={{ ...cell,
          borderLeft: isRight ? HALF_SEP : (isPendingSel ? (rainMode ? '2px solid #0284C7' : '2px solid #C9A84C') : isWalkInSelected ? '2px solid #3B82F6' : isBlockSelected ? '2px solid #DC2626' : isLinkSelected ? '2px solid #059669' : isRainRow ? '2px solid #38BDF8' : isBlocked ? '2px solid #F87171' : isClosed ? '2px solid #94A3B8' : isHeld ? '2px solid #A855F7' : isHighlight ? '2px solid #22C55E' : undefined),
          textAlign:'center', fontSize:'0.6rem', lineHeight:1.2,
          color: cap?.overridden ? '#D97706' : '#8a8070', fontWeight:700, width:28 }}
          onClick={onClick} onContextMenu={onCtx}>
          {cap ? `${cap.min}–${cap.max}` : ''}
        </td>
        {/* Seat name */}
        <td style={{ ...cell, fontWeight:800, color:NAVY, fontSize:'0.74rem', width:44 }}
          onClick={onClick} onContextMenu={onCtx}>
          {tableName}{isOverridden && <span style={{ color:'#D97706', fontSize:'0.6rem', verticalAlign:'super', marginLeft:1 }}>*</span>}
        </td>
        {/* Booking Name — every table of a group shows the name; continuation
            tables are muted + italic (ink-secondary holds ≥4.5:1 on the amber
            and boarded-green fills) so one group reads as one block, not
            several same-name bookings. First table stays full-weight ink. */}
        <td style={{ ...cell, maxWidth:0, textOverflow:'ellipsis',
          fontWeight: 500,
          color: isDO ? 'var(--color-ink-secondary)' : (assignment ? '#0A1628' : 'var(--color-ink-faint)'),
          fontStyle: isDO ? 'italic' : 'normal' }}
          onClick={onClick} onContextMenu={onCtx}>
          {assignment
            ? <>
                {booking?.booking_name||''}
                {isRainRow && (
                  <span style={{ marginLeft:6, verticalAlign:'middle', display:'inline-flex', alignItems:'center', gap:3, background:'#BAE6FD', color:'#075985', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.4px', padding:'1px 6px', borderRadius:10, textTransform:'uppercase', fontStyle:'normal', whiteSpace:'nowrap' }}>
                    Rain · own table {ownTablesOfBooking.join(', ') || '—'}
                  </span>
                )}
                {rainTablesOfBooking.length > 0 && (
                  <span style={{ display:'block', fontSize:'0.62rem', fontWeight:700, fontStyle:'normal', color:'#0369A1', marginTop:1 }}>
                    Reseated to {rainTablesOfBooking.join(', ')} (rain)
                  </span>
                )}
              </>
            : (isBlocked
                ? <span title={blockedMsg(block)} style={{ display:'inline-flex', alignItems:'center', gap:3, background:'#FECACA', color:'#991B1B', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.4px', padding:'1px 6px', borderRadius:10, textTransform:'uppercase' }}>● Blocked{!block.cruise_type ? ' · All cruises' : ''}{block.reason ? ` · ${block.reason}` : ''}</span>
                : isClosed
                ? <span title={closedMsg(closure)} style={{ display:'inline-flex', alignItems:'center', gap:3, background:'#CBD5E1', color:'#334155', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.4px', padding:'1px 6px', borderRadius:10, textTransform:'uppercase' }}>● Closed{closure.reason ? ` · ${closure.reason}` : ''}</span>
                : isHeld
                ? <span title={isLinkHeld ? 'Held by a staff-issued booking link' : 'Being booked online right now'} style={{ display:'inline-flex', alignItems:'center', gap:3, background:'#E9D5FF', color:'#6B21A8', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.4px', padding:'1px 6px', borderRadius:10, textTransform:'uppercase' }}>● {isLinkHeld ? 'Link' : 'Online'}</span>
                : isPendingSel
                ? <span style={{ display:'inline-flex', alignItems:'center', gap:3, background:'#EAD9A8', color:'#6B5310', fontSize:'0.5rem', fontWeight:800, letterSpacing:'0.4px', padding:'1px 6px', borderRadius:10, textTransform:'uppercase' }}>● Selected</span>
                : '')}
        </td>
        {/* A */}
        <td style={{ ...cell, textAlign:'center', color:'#3a4a6a', fontWeight:600, width:28 }} onClick={onClick}>
          {!isDO&&booking ? (booking.adults||'') : ''}
        </td>
        {/* CH */}
        <td style={{ ...cell, textAlign:'center', color:'#3a4a6a', fontWeight:600, width:28 }} onClick={onClick}>
          {!isDO&&booking ? (booking.children||'') : ''}
        </td>
        {/* I */}
        <td style={{ ...cell, textAlign:'center', color:'#5a6a8a', fontWeight:600, width:28 }} onClick={onClick}>
          {!isDO&&booking ? (booking.infants||'') : ''}
        </td>
        {/* TL */}
        <td style={{ ...cell, textAlign:'center', color:'#5a6a8a', fontWeight:600, width:28 }} onClick={onClick}>
          {!isDO&&booking ? (booking.tour_leaders||'') : ''}
        </td>
        {/* Boarded checkbox */}
        <td style={{ ...cell, textAlign:'center', width:46 }}
          onClick={e=>e.stopPropagation()}>
          {assignment && (
            <button
              onClick={()=>toggleBoarded(tableName, assignment.booking_id)}
              title={isBoarded?'Click to unboard':'Click to mark boarded'}
              style={{
                width:18, height:18, borderRadius:3,
                border: isBoarded ? 'none' : '1.5px solid var(--color-border-strong)',
                background: isBoarded ? '#22C55E' : 'transparent',
                cursor:'pointer', display:'inline-flex', alignItems:'center',
                justifyContent:'center', color:'#fff', fontSize:'0.65rem',
                fontWeight:900, padding:0, lineHeight:1, flexShrink:0,
              }}
            >
              {isBoarded?'✓':''}
            </button>
          )}
        </td>
      </>
    );
  };

  // ── Column header cells ─────────────────────────────────────────────────────
  const TH_LABELS = ['Cap','Seat','Booking Name','A','CH','I','TL','✓'];
  const thBase = {
    background:'var(--color-sunken)', fontWeight:700, fontSize:'0.58rem',
    textTransform:'uppercase', letterSpacing:'0.3px', color:'var(--color-ink-muted)',
    padding:'5px 5px', borderBottom:'2px solid #C9A84C88',
    borderRight:'1px solid var(--color-border-strong)',
    whiteSpace:'nowrap', userSelect:'none', position:'sticky', top:0, zIndex:5,
  };

  return (
    <div style={{ height:'100vh', display:'flex', flexDirection:'column', overflow:'hidden', background:'var(--color-sunken)' }}>

      {/* ── Big assigning banner — before header, full-width, impossible to miss ── */}
      {assigningBooking && (()=>{
        const totalPaxB = paxCount(assigningBooking);
        const allDone   = coveredPax>=totalPaxB && totalPaxB>0;
        const nSel      = pendingTables.size;
        return (
          <div className="ss-assign-banner" style={{
            background: rainMode ? '#0C4A6E' : allDone ? '#14532d' : NAVY,
            borderBottom: `3px solid ${rainMode ? '#38BDF8' : allDone ? '#22C55E' : '#C9A84C'}`,
          }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div className="ss-assign-banner-label">{rainMode ? 'Rain Reseating — Upper Deck Table Kept' : 'Now Assigning'}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:14, flexWrap:'wrap' }}>
                <span className="ss-assign-banner-name">{assigningBooking.booking_name}</span>
                {coveredPax>0 ? (
                  <span className={`ss-assign-banner-progress${!allDone?' ss-progress-pulse':''}`}
                    style={{ color: rainMode ? '#7DD3FC' : allDone ? '#4ade80' : '#C9A84C' }}>
                    {coveredPax} / {totalPaxB} pax covered{rainMode ? ' downstairs' : ''}{nSel>0 ? ` · ${nSel} table${nSel!==1?'s':''} selected` : ''}{allDone ? ' ✓' : ''}
                  </span>
                ) : (
                  <span className="ss-assign-banner-hint">
                    {rainMode
                      ? `${totalPaxB} pax · tap Middle Deck rows, then press Done to save`
                      : `${totalPaxB} pax · ${assigningBooking.deck?.replace(' Deck','')||'—'} · tap rows to select, then press Done to save`}
                  </span>
                )}
                {nSel>0 && (
                  <span className="ss-assign-banner-hint">not saved yet — press Done</span>
                )}
              </div>
            </div>
            <button
              className="ss-assign-banner-done-btn"
              disabled={committing}
              style={committing ? { opacity:0.6, cursor:'wait' } : undefined}
              onClick={()=>handleDoneAssigning()}
            >
              {committing ? 'Saving…' : 'Done Assigning ✓'}
            </button>
            <button
              style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid rgba(255,255,255,0.45)', background:'transparent', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}
              disabled={committing}
              onClick={()=>{ setPendingTables(new Set()); setAssigningBooking(null); setAssignWarning(''); }}
            >
              ✕ Cancel
            </button>
          </div>
        );
      })()}

      {/* ── Walk-in banner ──────────────────────────────────────────────────── */}
      {walkInMode && (
        <div style={{ background:'#1e3a8a', borderBottom:'3px solid #3B82F6', padding:'10px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#93c5fd', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:3 }}>Walk-in Mode Active</div>
            <div style={{ fontSize:'0.88rem', fontWeight:700, color:'#ffffff' }}>
              {walkInSelected.size === 0
                ? 'Click empty tables to select them for walk-in'
                : `${walkInSelected.size} table${walkInSelected.size!==1?'s':''} selected — capacity ${walkInCapRange.min}–${walkInCapRange.max} pax · [${[...walkInSelected].join(', ')}]`
              }
            </div>
          </div>
          {walkInSelected.size > 0 && (
            <button className="btn btn-gold" onClick={()=>setWalkInModal(true)}>
              Add Walk-in Booking →
            </button>
          )}
          <button
            style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid #93c5fd', background:'transparent', color:'#93c5fd', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}
            onClick={()=>{ setWalkInMode(false); setWalkInSelected(new Set()); }}
          >
            ✕ Cancel
          </button>
        </div>
      )}

      {/* ── Block-mode banner ───────────────────────────────────────────────── */}
      {blockMode && (
        <div style={{ background:'#7F1D1D', borderBottom:'3px solid #EF4444', padding:'10px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#FCA5A5', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:3 }}>Block Mode Active</div>
            <div style={{ fontSize:'0.88rem', fontWeight:700, color:'#ffffff' }}>
              {blockSelected.size === 0
                ? `Click tables to block them for ${cruise} — any deck, one action`
                : `${blockSelected.size} table${blockSelected.size!==1?'s':''} selected · [${[...blockSelected].join(', ')}]`
              }
            </div>
          </div>
          {blockSelected.size > 0 && (
            <button className="btn btn-gold" onClick={()=>setBlockModal(true)}>
              Set Dates & Block
            </button>
          )}
          <button
            style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid #FCA5A5', background:'transparent', color:'#FCA5A5', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', flexShrink:0, display:'inline-flex', alignItems:'center', gap:6 }}
            onClick={()=>{ setBlockMode(false); setBlockSelected(new Set()); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Send-link banner ────────────────────────────────────────────────── */}
      {linkMode && (
        <div style={{ background:'#064E3B', borderBottom:'3px solid #10B981', padding:'10px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#6EE7B7', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:3 }}>Send Link Mode Active</div>
            <div style={{ fontSize:'0.88rem', fontWeight:700, color:'#ffffff' }}>
              {linkSelected.size === 0
                ? 'Click empty tables to put them in a booking link — one deck, any tables'
                : `${linkSelected.size} table${linkSelected.size!==1?'s':''} selected — capacity ${linkCapRange.min}–${linkCapRange.max} pax · [${[...linkSelected].join(', ')}]`
              }
            </div>
          </div>
          {linkSelected.size > 0 && (
            <button className="btn btn-gold" onClick={()=>setLinkModal({ tables:[...linkSelected], deck:linkDeck })} style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
              <Link2 size={15} strokeWidth={2} aria-hidden="true" />Send booking link
            </button>
          )}
          <button
            style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid #6EE7B7', background:'transparent', color:'#6EE7B7', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}
            onClick={()=>{ setLinkMode(false); setLinkSelected(new Set()); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="ss-header">

        {/* Row 1: date/cruise identity + action buttons */}
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:8 }}>
          <button className="back-btn" onClick={()=>onNavigate('operations')}>← Operations</button>

          {/* Date navigator */}
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <button className="ss-arrow-btn" onClick={()=>shiftDay(-1)} aria-label="Previous day">←</button>
            <input
              type="date" value={inputDate}
              onChange={e=>{ setInputDate(e.target.value); setAssigningBooking(null); setAssignWarning(''); }}
              style={{ background:'#fff', border:'2px solid #C9A84C', borderRadius:9, padding:'8px 11px', color:NAVY, fontSize:'1rem', fontWeight:700, outline:'none', colorScheme:'light', letterSpacing:'0.2px' }}
            />
            <button className="ss-arrow-btn" onClick={()=>shiftDay(1)} aria-label="Next day">→</button>
          </div>

          {/* Cruise selector */}
          <select
            value={cruise}
            onChange={e=>{ setCruise(e.target.value); setAssigningBooking(null); setAssignWarning(''); }}
            style={{ background:NAVY, border:'none', borderRadius:9, padding:'9px 14px', color:'#fff', fontSize:'0.95rem', fontWeight:700, outline:'none', cursor:'pointer', letterSpacing:'0.2px' }}
          >
            {(cruiseSettings.length > 0 ? cruiseSettings : [{cruise_name:'Sunset Cruise'},{cruise_name:'Sundown Cruise'},{cruise_name:'Dinner Cruise'}]).map(s=><option key={s.cruise_name} value={s.cruise_name}>{s.cruise_name}</option>)}
          </select>

          {/* Action buttons */}
          <button className="btn btn-navy btn-sm" onClick={handleAutoAssign} disabled={loading||unassignedBookings.length===0}>
            <Zap size={16} strokeWidth={1.75} aria-hidden="true" />Auto Assign
          </button>
          <button
            style={{
              padding:'6px 12px', borderRadius:8, fontSize:'0.82rem', fontWeight:700, cursor:'pointer',
              background: walkInMode ? GOLD : 'transparent',
              border: `1.5px solid ${walkInMode ? GOLD : 'var(--color-border-strong)'}`,
              color: walkInMode ? '#0A1628' : 'var(--color-ink-secondary)',
              transition:'background 0.15s, color 0.15s, border-color 0.15s', whiteSpace:'nowrap',
              display:'inline-flex', alignItems:'center', gap:6,
            }}
            onClick={() => {
              if (walkInMode) { setWalkInMode(false); setWalkInSelected(new Set()); }
              else { setWalkInMode(true); setBlockMode(false); setBlockSelected(new Set()); setLinkMode(false); setLinkSelected(new Set()); setAssigningBooking(null); setAssignWarning(''); }
            }}
          >
            <Footprints size={16} strokeWidth={1.75} aria-hidden="true" />Walk-in
          </button>
          <button
            style={{
              padding:'6px 12px', borderRadius:8, fontSize:'0.82rem', fontWeight:700, cursor:'pointer',
              background: blockMode ? GOLD : 'transparent',
              border: `1.5px solid ${blockMode ? GOLD : 'var(--color-border-strong)'}`,
              color: blockMode ? '#0A1628' : 'var(--color-ink-secondary)',
              transition:'background 0.15s, color 0.15s, border-color 0.15s', whiteSpace:'nowrap',
              display:'inline-flex', alignItems:'center', gap:6,
            }}
            onClick={() => {
              if (blockMode) { setBlockMode(false); setBlockSelected(new Set()); }
              else { setBlockMode(true); setWalkInMode(false); setWalkInSelected(new Set()); setLinkMode(false); setLinkSelected(new Set()); setAssigningBooking(null); setAssignWarning(''); }
            }}
          >
            <Ban size={16} strokeWidth={1.75} aria-hidden="true" />Block
          </button>
          <button
            style={{
              padding:'6px 12px', borderRadius:8, fontSize:'0.82rem', fontWeight:700, cursor:'pointer',
              background: linkMode ? GOLD : 'transparent',
              border: `1.5px solid ${linkMode ? GOLD : 'var(--color-border-strong)'}`,
              color: linkMode ? '#0A1628' : 'var(--color-ink-secondary)',
              transition:'background 0.15s, color 0.15s, border-color 0.15s', whiteSpace:'nowrap',
              display:'inline-flex', alignItems:'center', gap:6,
            }}
            onClick={() => {
              if (linkMode) { setLinkMode(false); setLinkSelected(new Set()); }
              else { setLinkMode(true); setWalkInMode(false); setWalkInSelected(new Set()); setBlockMode(false); setBlockSelected(new Set()); setAssigningBooking(null); setAssignWarning(''); }
            }}
          >
            <Link2 size={16} strokeWidth={1.75} aria-hidden="true" />Send link
          </button>
          <button ref={linksBtnRef} className="btn btn-outline btn-sm" onClick={openLinks} aria-haspopup="dialog" aria-expanded={linksOpen} title="Every booking link, across all dates">
            <ClipboardList size={16} strokeWidth={1.75} aria-hidden="true" />All links
          </button>
          <button className="btn btn-outline btn-sm" onClick={()=>setExtraModal(true)} disabled={loading}>
            + Extra Table
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={()=>onNavigate('daily-tracker', displayDate, { cruise_type: cruise })}
          >
            ⇢ Tracker
          </button>
          <button
            onClick={()=>setUnassignAllConfirm(true)}
            disabled={loading||assignments.length===0}
            style={{ padding:'6px 12px', fontSize:'0.72rem', fontWeight:700, borderRadius:7, border:'1.5px solid #fca5a5', background:'transparent', color:'#dc2626', cursor:'pointer', opacity: assignments.length===0 ? 0.4 : 1, transition:'background 0.15s, color 0.15s', whiteSpace:'nowrap' }}
            onMouseEnter={e=>{ if(assignments.length>0){ e.target.style.background='#fee2e2'; } }}
            onMouseLeave={e=>{ e.target.style.background='transparent'; }}
          >
            Unassign All
          </button>

          {/* Prominent date + cruise label */}
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div style={{ fontSize:'1.15rem', fontWeight:900, color:GOLD, letterSpacing:'0.2px', lineHeight:1.1 }}>
              {formatDateWords(inputDate)}
            </div>
            <div style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--color-ink-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginTop:2 }}>
              {cruise}
            </div>
          </div>
        </div>

        {/* Success toast */}
        {clearSuccess && (
          <div style={{ marginBottom:6, background:'#DCFCE7', border:'1px solid #22C55E', borderRadius:8, padding:'6px 12px', fontSize:'0.8rem', color:'#15803d', fontWeight:600, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>✓ {clearSuccess}</span>
            <button onClick={()=>setClearSuccess('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#15803d', fontSize:'1rem', padding:'0 4px' }}>×</button>
          </div>
        )}
        {/* Summary bar */}
        <div className="ss-summary-bar">
          <SumStat label="Total Pax"    value={totalPax} />
          <div className="ss-stat-sep"/>
          <SumStat label="Assigned"     value={assignedPax}            color="var(--color-accent-strong)" />
          <div className="ss-stat-sep"/>
          <SumStat label="Unassigned"   value={totalPax-assignedPax}   color={(totalPax-assignedPax)>0?'#DC2626':'#15803d'} />
          <div className="ss-stat-sep"/>
          <SumStat label="Boarded"      value={boardedPax}             color="#15803d" />
          <div className="ss-stat-sep"/>
          <SumStat label="Yet to Board" value={assignedPax-boardedPax} color={(assignedPax-boardedPax)>0?'#D97706':'#9a8e7a'} />
        </div>

        {wholeCruiseClosure && (
          <div style={{ marginTop:8, background:'#E2E8F0', border:'1px solid #94A3B8', borderRadius:8, padding:'8px 14px', fontSize:'0.82rem', color:'#334155', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
            <Lock size={16} strokeWidth={1.9} aria-hidden="true" style={{ flexShrink:0 }} />
            <span>
              Online booking closed — {cruise} is closed on {displayDate}{wholeCruiseClosure.reason ? ` — ${wholeCruiseClosure.reason}` : ''}. Existing seating is unaffected; staff can still record a Private Charter.
            </span>
          </div>
        )}

        {assignWarning && (
          <div style={{ marginTop:8, background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:8, padding:'6px 12px', fontSize:'0.8rem', color:'#92400e', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" /> {assignWarning}</span>
            <button onClick={()=>setAssignWarning('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#92400e', fontSize:'1rem', padding:'0 4px' }}>×</button>
          </div>
        )}
      </div>

      {/* ── Main layout ─────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden' }}>

        {/* Seating spreadsheet */}
        <div style={{ flex:1, overflowY:'auto', overflowX:'auto', padding:'10px 12px' }}>
          {loading && <p style={{ color:'var(--color-ink-faint)', fontSize:'0.85rem', padding:'8px 0' }}>Loading…</p>}

          <table style={{ borderCollapse:'collapse', width:'100%', tableLayout:'fixed', background:'#fff', borderRadius:10, overflow:'hidden', boxShadow:'0 1px 8px rgba(10,22,40,0.07)' }}>
            <colgroup>
              {/* Left side */}
              <col style={{width:28}}/><col style={{width:44}}/><col/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:46}}/>
              {/* Right side */}
              <col style={{width:28}}/><col style={{width:44}}/><col/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:30}}/><col style={{width:46}}/>
            </colgroup>

            <thead>
              <tr>
                {[...TH_LABELS, ...TH_LABELS].map((h,i)=>(
                  <th key={i} style={{ ...thBase,
                    textAlign: (i===2||i===10) ? 'left' : 'center',
                    borderLeft: i===8 ? '3px solid var(--color-border-strong)' : undefined }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ─ Middle Deck ────────────────────────────────────────────── */}
              <SectionHeader label="Middle Deck" />
              {MIDDLE_PAIRS.map(([left,right],i)=>(
                <tr key={`m${i}`} style={{}}>
                  {renderCells(left, false)}
                  {renderCells(right, true)}
                </tr>
              ))}

              {/* ─ Upper Deck ─────────────────────────────────────────────── */}
              {showUpper && <>
                <SectionHeader label="UPPER DECK" />
                {UPPER_PAIRS.map(([left,right],i)=>(
                  <tr key={`u${i}`} style={{}}>
                    {renderCells(left, false)}
                    {renderCells(right, true, right === null)}
                  </tr>
                ))}
              </>}

              {/* ─ Extra Tables ───────────────────────────────────────────── */}
              {extraTables.length>0 && <>
                <SectionHeader label="Extra Tables" />
                {extraPairs.map(([left,right],i)=>(
                  <tr key={`e${i}`} style={{}}>
                    {renderCells(left, false)}
                    {renderCells(right, true)}
                  </tr>
                ))}
              </>}
            </tbody>
          </table>
        </div>

        {/* Unassigned panel */}
        <div style={{ width:248, flexShrink:0, background:'#fff', borderLeft:'1px solid var(--color-border)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:'1px solid var(--color-border)', flexShrink:0 }}>
            <span style={{ fontWeight:700, fontSize:'0.8rem', color:NAVY }}>Unassigned</span>
            <span style={{
              background: unassignedBookings.length>0?'#FEF3C7':'#DCFCE7',
              color: unassignedBookings.length>0?'#92400e':'#15803d',
              border: `1px solid ${unassignedBookings.length>0?'#F59E0B':'#22C55E'}`,
              borderRadius:20, padding:'2px 10px', fontSize:'0.68rem', fontWeight:800,
            }}>{unassignedBookings.length}</span>
          </div>

          {unassignedBookings.length===0 ? (
            <div style={{ textAlign:'center', padding:'28px 12px', color:'#15803d', fontSize:'0.82rem', fontWeight:600 }}>
              All bookings assigned ✓
            </div>
          ) : (
            <div style={{ flex:1, overflowY:'auto', padding:'10px 12px', display:'flex', flexDirection:'column', gap:7 }}>
              {unassignedBookings.map(b=>{
                const totalP = paxVisible(b);
                const isSel  = assigningBooking?.id===b.id;
                return (
                  <div
                    key={b.id}
                    onClick={()=>{ if (!walkInMode && !blockMode && !linkMode) setAssigningBooking(prev=>prev?.id===b.id?null:b); }}
                    style={{
                      background: isSel?'#EFF6FF':'var(--color-sunken)',
                      border: isSel?'2px solid #3B82F6':'1.5px solid var(--color-border)',
                      borderRadius:9, padding:'8px 10px', cursor:'pointer',
                      boxShadow: isSel?'0 0 0 3px #3B82F622':undefined,
                    }}
                  >
                    <div style={{ display:'flex', alignItems:'baseline', gap:5, flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color:NAVY, wordBreak:'break-word' }}>
                        {b.booking_name}
                      </span>
                      <span style={{ fontSize:'0.82rem', fontWeight:800, color:'#C9A84C', whiteSpace:'nowrap' }}>
                        — {totalP} pax
                      </span>
                    </div>
                    {b.partner_name && (
                      <div style={{ fontSize:'0.66rem', color:'var(--color-ink-muted)', marginTop:2 }}>{b.partner_name}</div>
                    )}
                    <div style={{ marginTop:5 }}>
                      <span style={{
                        display:'inline-block',
                        background: b.deck==='Upper Deck' ? NAVY : '#C9A84C',
                        color: b.deck==='Upper Deck' ? '#ffffff' : NAVY,
                        fontSize:'0.66rem', fontWeight:700,
                        padding:'2px 8px', borderRadius:20,
                        letterSpacing:'0.2px', whiteSpace:'nowrap',
                      }}>
                        {b.deck||'—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div style={{ padding:'10px 14px', borderTop:'1px solid var(--color-border)', flexShrink:0 }}>
            <div style={{ fontSize:'0.57rem', fontWeight:700, color:'var(--color-ink-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Legend</div>
            {[
              { bg:'#FEF3C7', bd:'#D97706', label:'Assigned' },
              { bg:'#DCFCE7', bd:'#22C55E', label:'Boarded' },
              { bg:'#BBF7D0', bd:'#22C55E', label:'Suitable — click to assign' },
              { bg:'#F3E8FF', bd:'#A855F7', label:'Booking in progress (online)' },
              { bg:'#E2E8F0', bd:'#94A3B8', label:'Closed / Reserved (online)' },
              { bg:'#FEE2E2', bd:'#F87171', label:'Blocked (staff)' },
              { bg:'#ffffff',    bd:'#c8bfb0', label:'Empty' },
            ].map(({bg,bd,label})=>(
              <div key={label} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <div style={{ width:14, height:14, background:bg, border:`2px solid ${bd}`, borderRadius:2, flexShrink:0 }} />
                <span style={{ fontSize:'0.67rem', color:'var(--color-ink-muted)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{ position:'fixed', top:contextMenu.y, left:contextMenu.x, background:'#fff', border:'1px solid var(--color-border)', borderRadius:9, boxShadow:'0 6px 20px rgba(10,22,40,0.14)', zIndex:2000, minWidth:220 }}
          onClick={e=>e.stopPropagation()}
        >
          <button
            style={{ width:'100%', padding:'11px 16px', textAlign:'left', background:'none', border:'none', cursor:'pointer', fontSize:'0.85rem', color:NAVY }}
            onClick={()=>{
              const cap = getCap(contextMenu.tableName);
              setOverrideModal({ tableName:contextMenu.tableName, current:cap||{min:'',max:''} });
              setContextMenu(null);
            }}
          >
            ✎ Override Capacity for This Date
          </button>
        </div>
      )}

      {/* Modals */}
      {blockModal && blockSelected.size > 0 && (
        <BlockTablesModal
          selectedTables={blockSelected}
          defaultDate={inputDate}
          cruise={cruise}
          onSave={handleBlockSave}
          onClose={()=>setBlockModal(false)}
        />
      )}
      {blockAction && (
        <ConfirmDialog
          heading={`${blockAction.tableName} is blocked`}
          message={`Blocked ${blockRangeLabel(blockAction.block)} for ${blockAction.block.cruise_type || 'all cruises'}${blockAction.block.reason ? ` — ${blockAction.block.reason}` : ''}. No new booking can be seated here while blocked. Remove the block for the whole range?`}
          onClose={()=>setBlockAction(null)}
          actions={[
            { label:'Keep Block', style:{ background:'var(--color-sunken)', color:NAVY, border:'1.5px solid var(--color-border-strong)' }, onClick:()=>setBlockAction(null) },
            { label:'Remove Block', style:{ background:'#DC2626', color:'#fff' }, onClick:()=>removeBlock(blockAction.block) },
          ]}
        />
      )}
      {overrideModal && <CapacityOverrideModal tableName={overrideModal.tableName} current={overrideModal.current} onSave={saveOverride} onClose={()=>setOverrideModal(null)} />}
      {extraModal    && <ExtraTableModal onSave={addExtraTable} onClose={()=>setExtraModal(false)} />}
      {autoPreview   && <AutoAssignModal preview={autoPreview} accepting={accepting} onAccept={acceptAutoAssign} onClose={()=>setAutoPreview(null)} />}
      {walkInModal && walkInDeck && (
        <WalkInModal
          selectedTables={walkInSelected}
          getCap={getCap}
          displayDate={displayDate}
          cruise={cruise}
          deck={walkInDeck}
          onSave={handleWalkInSave}
          onClose={()=>setWalkInModal(false)}
        />
      )}
      {linksOpen && <TableLinksPanel onClose={closeLinks} />}
      {linkModal && (
        <SendLinkModal
          selectedTables={linkModal.tables}
          displayDate={displayDate}
          inputDate={inputDate}
          cruise={cruise}
          deck={linkModal.deck}
          onCreated={()=>fetchHolds()}   // the new link hold shows on the sheet straight away (prunes linkSelected, not the snapshot)
          onClose={(created)=>{
            setLinkModal(null);
            if (created) { setLinkMode(false); setLinkSelected(new Set()); }
          }}
        />
      )}

      {/* Walk-in post-save: download ticket or skip */}
      {walkInPostSave && (
        <div className="ss-modal-backdrop">
          <div className="ss-modal" style={{ maxWidth:420 }}>
            <div style={{ width:44, height:44, background:'#DCFCE7', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem', marginBottom:16 }}>✓</div>
            <h3 style={{ fontSize:'1rem', fontWeight:800, color:NAVY, marginBottom:6 }}>Walk-in booking added!</h3>
            <p style={{ fontSize:'0.88rem', color:'#4a5a7a', marginBottom:4 }}>
              {walkInPostSave.tableList.length > 0
                ? <><strong>{walkInPostSave.booking.booking_name}</strong> assigned to <strong>{walkInPostSave.tableList.join(', ')}</strong></>
                : <><strong>{walkInPostSave.booking.booking_name}</strong> saved — left in Unassigned.</>}
            </p>
            {walkInPostSave.heldTables?.length > 0 && (
              <p style={{ fontSize:'0.8rem', background:'#FEF3C7', border:'1px solid #F59E0B', color:'#92400e', borderRadius:8, padding:'8px 12px', margin:'8px 0 12px', fontWeight:600 }}>
                {walkInPostSave.heldTables.length === 1
                  ? `Table ${walkInPostSave.heldTables[0]} is`
                  : `Tables ${walkInPostSave.heldTables.join(', ')} are`} being booked online right now. Left in Unassigned — try again in a few minutes.
              </p>
            )}
            {walkInPostSave.reference && (
              <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:20 }}>Reference: <strong>{walkInPostSave.reference}</strong></p>
            )}
            {!walkInPostSave.reference && <div style={{ marginBottom:20 }} />}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button
                className="btn btn-gold"
                onClick={async () => {
                  await downloadTicket(walkInPostSave.booking, walkInPostSave.reference);
                  setWalkInPostSave(null);
                }}
              >
                ↓ Download Ticket
              </button>
              <button
                className="btn btn-whatsapp"
                onClick={() => {
                  setSendTarget({ booking: walkInPostSave.booking, reference: walkInPostSave.reference });
                  setWalkInPostSave(null);
                }}
              >
                <MessageCircle size={15} strokeWidth={1.75} aria-hidden="true" />Send via WhatsApp
              </button>
              <button className="btn btn-outline" onClick={()=>setWalkInPostSave(null)}>Skip</button>
            </div>
          </div>
        </div>
      )}
      {/* Send ticket via WhatsApp — reuses the same modal as ViewBookings */}
      {sendTarget && (
        <SendTicketModal
          booking={sendTarget.booking}
          reference={sendTarget.reference || null}
          onClose={() => setSendTarget(null)}
        />
      )}

      {tableAction   && (
        <TableActionModal
          tableName={tableAction.tableName}
          booking={tableAction.booking}
          assignment={tableAction.assignment}
          bookingTableCount={bookingTableCount[tableAction.assignment?.booking_id]||1}
          onReassign={handleReassign}
          onUnassign={handleUnassign}
          onClose={()=>setTableAction(null)}
          rainTables={rainTablesByBooking[tableAction.assignment?.booking_id] || []}
          onRainMove={()=>{
            const b = tableAction.booking;
            setTableAction(null);
            setRainMode(true);
            setAssigningBooking(b);
          }}
          onEndRain={()=>{
            const id = tableAction.assignment?.booking_id;
            setTableAction(null);
            if (id) endRainSeating(id);
          }}
        />
      )}

      {/* Unassign All confirmation */}
      {unassignAllConfirm && (
        <ConfirmDialog
          heading="Unassign all bookings?"
          message={`Are you sure you want to unassign all bookings for ${cruise} on ${formatDateWords(inputDate)}? This will also clear all boarded status. This cannot be undone.`}
          onClose={()=>setUnassignAllConfirm(false)}
          actions={[
            { label:'Cancel', style:{ background:'var(--color-sunken)', color:NAVY, border:'1.5px solid var(--color-border-strong)' }, onClick:()=>setUnassignAllConfirm(false) },
            { label:'Unassign All', style:{ background:'#DC2626', color:'#fff' }, onClick:unassignAll },
          ]}
        />
      )}

      {/* Done with unseated pax — centered dialog */}
      {doneConfirm && assigningBooking && (
        <ConfirmDialog
          heading="Not all pax are seated"
          message={`Only ${coveredPax} of ${paxCount(assigningBooking)} pax are covered. Are you sure you want to finish assigning?`}
          onClose={()=>setDoneConfirm(false)}
          actions={[
            { label:'Continue Assigning', style:{ background:'var(--color-sunken)', color:NAVY, border:'1.5px solid var(--color-border-strong)' }, onClick:()=>setDoneConfirm(false) },
            { label:'Finish Anyway', style:{ background:'#DC2626', color:'#fff' }, onClick:()=>{
              setDoneConfirm(false);
              handleDoneAssigning({ skipMin:true, skipPartial:true });
            }},
          ]}
        />
      )}

      {/* Extra table beyond party size — asked once per group, then silent */}
      {extraTablePrompt && assigningBooking && (
        <ConfirmDialog
          heading="Group already covered"
          message={`This group's ${extraTablePrompt.pax} guest${extraTablePrompt.pax!==1?'s':''} ${extraTablePrompt.pax!==1?'are':'is'} already covered by ${extraTablePrompt.nTables} table${extraTablePrompt.nTables!==1?'s':''}. Add ${extraTablePrompt.tableName} anyway?`}
          onClose={()=>setExtraTablePrompt(null)}
          actions={[
            { label:'Cancel', style:{ background:'var(--color-sunken)', color:NAVY, border:'1.5px solid var(--color-border-strong)' }, onClick:()=>setExtraTablePrompt(null) },
            { label:'Add Table', style:{ background:NAVY, color:'#fff' }, onClick:()=>{
              setPendingTables(prev=>new Set(prev).add(extraTablePrompt.tableName));
              setExtraConfirmed(true);
              setExtraTablePrompt(null);
            }},
          ]}
        />
      )}

      {/* Capacity override — centered dialog */}
      {capOverridePrompt && (
        <ConfirmDialog
          heading="Table capacity mismatch"
          message={capOverridePrompt.message}
          onClose={()=>setCapOverridePrompt(null)}
          actions={[
            { label:'OK', style:{ background:'var(--color-sunken)', color:NAVY, border:'1.5px solid var(--color-border-strong)' }, onClick:()=>setCapOverridePrompt(null) },
            { label:'Override & Assign', style:{ background:'#DC2626', color:'#fff' }, onClick:()=>{
              setOverriddenTables(prev=>new Set([...prev, ...pendingTables]));
              setCapOverridePrompt(null);
              handleDoneAssigning({ skipMin:true });
            }},
          ]}
        />
      )}
    </div>
  );
}

function SumStat({ label, value, color }) {
  return (
    <div className="ss-stat">
      <span className="ss-stat-label">{label}</span>
      <span className="ss-stat-value" style={color?{color}:undefined}>{value}</span>
    </div>
  );
}
